import { Constraint, Role, ShiftTag, User, SickNote, UsersByRole, BookingStats, ShiftStats, WithdrawalRequest, ShiftRoleRequirement, NewsPost } from '../types/index';
import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from '../components/Header';
import { adminService, shiftsService, supervisorService, volunteerApplicationService, adminWithdrawalService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

const getTagColor = (tagName: string): string => {
  const name = tagName.toLowerCase();
  
  if (name.includes('night')) return 'bg-indigo-900 text-indigo-100';
  if (name.includes('evening')) return 'bg-purple-600 text-purple-100';
  if (name.includes('morning')) return 'bg-yellow-500 text-yellow-900';
  if (name.includes('music')) return 'bg-pink-600 text-pink-100';
  if (name.includes('day')) return 'bg-orange-500 text-orange-100';
  if (name.includes('weekend')) return 'bg-blue-600 text-blue-100';
  
  return 'bg-gray-600 text-gray-100';
};

const getTotalRoleSpots = (shift: any): number => {
  if (!Array.isArray(shift?.role_requirements)) return 0;
  return shift.role_requirements.reduce((sum: number, req: any) => sum + (Number(req?.max_count) || 0), 0);
};

const getTotalRoleBooked = (shift: any): number => {
  if (!Array.isArray(shift?.role_requirements)) return 0;
  return shift.role_requirements.reduce((sum: number, req: any) => sum + (Number(req?.current_count) || 0), 0);
};

const formatAdminShiftLabel = (shift: any): string => {
  if (!shift) return 'Shift';
  const start = String(shift.start_time || '').substring(0, 16).replace('T', ' ');
  const end = String(shift.end_time || '').substring(0, 16).replace('T', ' ');
  const startDate = start.split(' ')[0] || '';
  const endDate = end.split(' ')[0] || '';
  const nextDaySuffix = startDate && endDate && startDate !== endDate ? ' (next day)' : '';
  return `${shift.title || 'Shift'} · ${start}${nextDaySuffix ? '' : ''} - ${end}${nextDaySuffix}`;
};

const buildSwapCandidates = (assignmentsByShift: Record<number, any[]>, shifts: any[]) => {
  const shiftById = new Map<number, any>(shifts.map((shift) => [Number(shift.id), shift]));
  const byUser = new Map<number, any>();

  Object.entries(assignmentsByShift).forEach(([shiftId, volunteers]) => {
    const shift = shiftById.get(Number(shiftId));
    (volunteers || []).forEach((volunteer: any) => {
      const existing = byUser.get(Number(volunteer.id)) || {
        id: Number(volunteer.id),
        name: volunteer.name || volunteer.email || 'Unknown volunteer',
        email: volunteer.email || '',
        role: volunteer.role || 'volunteer',
        bookings: [],
      };

      existing.bookings.push({
        booking_id: Number(volunteer.booking_id),
        shift_id: Number(shiftId),
        shift,
        role: volunteer.role || 'volunteer',
        attendance_status: volunteer.attendance_status || 'pending',
      });
      byUser.set(Number(volunteer.id), existing);
    });
  });

  return Array.from(byUser.values()).sort((a, b) => a.name.localeCompare(b.name));
};

const urlRegex = /(https?:\/\/[^\s]+)/g;

const renderTextWithLinks = (text: string) => {
  return text.split('\n').map((line, lineIndex, lines) => {
    const parts = line.split(urlRegex);
    return (
      <React.Fragment key={lineIndex}>
        {parts.map((part, partIndex) => {
          if (/^https?:\/\/[^\s]+$/.test(part)) {
            return (
              <a
                key={`${lineIndex}-${partIndex}`}
                href={part}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline break-all"
              >
                {part}
              </a>
            );
          }
          return <React.Fragment key={`${lineIndex}-${partIndex}`}>{part}</React.Fragment>;
        })}
        {lineIndex < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });
};

export function AdminDashboard() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const formatDateInput = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  const getDefaultLogDateRange = () => {
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - 7);
    return {
      from: formatDateInput(fromDate),
      to: formatDateInput(toDate),
    };
  };
  const formatBytes = (bytes: number) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, exponent);
    return `${value.toFixed(value >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
  };
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [constraints, setConstraints] = useState<Constraint[]>([]);
  const [tags, setTags] = useState<ShiftTag[]>([]);
  const [shifts, setShifts] = useState<any[]>([]);
  const [bookingStats, setBookingStats] = useState<BookingStats | null>(null);
  const [shiftStats, setShiftStats] = useState<ShiftStats | null>(null);
  const [usersByRole, setUsersByRole] = useState<UsersByRole[]>([]);
  const [assignmentsByShift, setAssignmentsByShift] = useState<Record<number, any[]>>({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [addTargetShiftId, setAddTargetShiftId] = useState<number | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [swapVolunteer1Search, setSwapVolunteer1Search] = useState('');
  const [swapVolunteer2Search, setSwapVolunteer2Search] = useState('');
  const [swapVolunteer1Id, setSwapVolunteer1Id] = useState<number | null>(null);
  const [swapVolunteer2Id, setSwapVolunteer2Id] = useState<number | null>(null);
  const [swapBooking1Id, setSwapBooking1Id] = useState<number | null>(null);
  const [swapBooking2Id, setSwapBooking2Id] = useState<number | null>(null);
  const [swappingAssignments, setSwappingAssignments] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignBookingId, setReassignBookingId] = useState<number | null>(null);
  const [reassignTargetShiftId, setReassignTargetShiftId] = useState<number | ''>('');
  const [reassignContext, setReassignContext] = useState<'default' | 'sick-note'>('default');
  const [reassignOriginalShiftId, setReassignOriginalShiftId] = useState<number | null>(null);
  const [pendingSickNoteId, setPendingSickNoteId] = useState<number | null>(null);
  const [pendingSickNoteAdminNotes, setPendingSickNoteAdminNotes] = useState('');
  const [pendingSickRequireDoctor, setPendingSickRequireDoctor] = useState<boolean | undefined>(undefined);
  const [showUserShiftsModal, setShowUserShiftsModal] = useState(false);
  const [modalUserId, setModalUserId] = useState<number | null>(null);
  const [modalUserName, setModalUserName] = useState<string>('');
  const [modalUserShifts, setModalUserShifts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [applications, setApplications] = useState<any[]>([]);
  const [deviations, setDeviations] = useState<any[]>([]);
  const [sickNotes, setSickNotes] = useState<SickNote[]>([]);
  const [showCompletedSickNotes, setShowCompletedSickNotes] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [withdrawalSickNoteId, setWithdrawalSickNoteId] = useState<number | null>(null);
  const [withdrawalVolunteerName, setWithdrawalVolunteerName] = useState('');
  const [withdrawalShifts, setWithdrawalShifts] = useState<any[]>([]);
  const [showUserWithdrawalModal, setShowUserWithdrawalModal] = useState(false);
  const [withdrawingUserId, setWithdrawingUserId] = useState<number | null>(null);
  const [withdrawingUserName, setWithdrawingUserName] = useState('');
  const [withdrawingUserShifts, setWithdrawingUserShifts] = useState<any[]>([]);
  const [showInactiveUsers, setShowInactiveUsers] = useState(false);
  const [showNotOnWhatsAppOnly, setShowNotOnWhatsAppOnly] = useState(false);
  const [showWithoutShiftsOnly, setShowWithoutShiftsOnly] = useState(false);
  const [userImportText, setUserImportText] = useState('');
  const [userImportError, setUserImportError] = useState('');
  const [userImportResult, setUserImportResult] = useState<{ created: number; updated: number; skipped: number; errors: string[] } | null>(null);
  const [userImportLoading, setUserImportLoading] = useState(false);
  const [showUserImportForm, setShowUserImportForm] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderSubject, setReminderSubject] = useState('Reminder: Book your shifts for %event%');
  const [reminderTemplate, setReminderTemplate] = useState('Hi %firstname%,\n\nWe noticed you do not have any shifts booked yet. Please sign in and book your shifts: %frontend_url%\n\nBest,\n%event%');
  const [sendingReminder, setSendingReminder] = useState(false);

  const reminderRecipients = users
    .filter((u) => ['volunteer', 'supervisor', 'admin'].includes(u.role))
    .filter((u) => !u.withdrawn)
    .filter((u) => u.application_status !== 'rejected')
    .filter((u) => Number(u.shifts_booked || 0) === 0)
    .sort((a, b) => {
      const aName = (a.full_name || a.name || a.email || '').toLowerCase();
      const bName = (b.full_name || b.name || b.email || '').toLowerCase();
      return aName.localeCompare(bName);
    });
  const [waitlistImportText, setWaitlistImportText] = useState('');
  const [waitlistImportError, setWaitlistImportError] = useState('');
  const [waitlistImportResult, setWaitlistImportResult] = useState<{ created: number; updated: number; skipped: number; errors: string[] } | null>(null);
  const [waitlistImportLoading, setWaitlistImportLoading] = useState(false);
  const [showWaitlistImportForm, setShowWaitlistImportForm] = useState(false);
  const [waitlistMoveTargets, setWaitlistMoveTargets] = useState<Record<number, string>>({});
  const [movingWaitlistUserId, setMovingWaitlistUserId] = useState<number | null>(null);
  const [openWaitlistMoveUserId, setOpenWaitlistMoveUserId] = useState<number | null>(null);
  const [selectedWaitlistUserIds, setSelectedWaitlistUserIds] = useState<Set<number>>(new Set());
  const [showBatchInviteConfirm, setShowBatchInviteConfirm] = useState(false);
  const [sendingBatchOffers, setSendingBatchOffers] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'applications' | 'waitlist' | 'shifts' | 'assignments' | 'users' | 'constraints' | 'settings' | 'deviations' | 'sick-notes' | 'withdrawals' | 'logs' | 'news'>('overview');
  const [waitlistUsers, setWaitlistUsers] = useState<User[]>([]);
  const [newsPosts, setNewsPosts] = useState<NewsPost[]>([]);
  const [newsForm, setNewsForm] = useState({ title: '', content: '', is_published: true });
  const [editingNewsId, setEditingNewsId] = useState<number | null>(null);
  const [withdrawalRequests, setWithdrawalRequests] = useState<WithdrawalRequest[]>([]);
  const [showApproveWithdrawalModal, setShowApproveWithdrawalModal] = useState(false);
  const [approveWithdrawalTarget, setApproveWithdrawalTarget] = useState<WithdrawalRequest | null>(null);
  const [approveWithdrawalDeleteShifts, setApproveWithdrawalDeleteShifts] = useState(false);
  const [approveWithdrawalAdminNotes, setApproveWithdrawalAdminNotes] = useState('');
  const [approvingWithdrawalId, setApprovingWithdrawalId] = useState<number | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [imagePreviewTitle, setImagePreviewTitle] = useState<string>('');
  const [todos, setTodos] = useState<{ id: number; text: string; created_at: string }[]>([]);
  const [todoInput, setTodoInput] = useState('');

  // Sync activeTab with URL on load and change
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['overview', 'applications', 'waitlist', 'shifts', 'assignments', 'users', 'constraints', 'settings', 'deviations', 'sick-notes', 'withdrawals', 'logs', 'news'].includes(tabParam)) {
      setActiveTab(tabParam as any);
    }
  }, [searchParams]);

  const handleTabChange = (newTab: typeof activeTab) => {
    setActiveTab(newTab);
    setSearchParams({ tab: newTab }, { replace: true });
  };

  const [newTag, setNewTag] = useState({ name: '', description: '' });
  const [globalSettings, setGlobalSettings] = useState({
    event_name: 'Roskilde Festival',
    min_hours_between_shifts: 0,
    show_shift_title: true,
    show_shift_location: true,
    show_next_shift_box: true,
    show_available_spots: true,
    show_waitlist_position: true,
    volunteer_login_waitlist_modal_enabled: false,
    volunteer_login_new_signups_closed: false,
    whatsapp_group_join_url: '',
    whatsapp_group_confirmation_code: '',
    booking_enabled: 'booking-enabled' as 'no-access' | 'view-only' | 'booking-enabled',
    sick_note_enabled: false,
    withdrawal_permitted: true,
    fill_threshold_critical: 30,
    fill_threshold_warning: 50,
    fill_threshold_low: 80,
    booking_capacity_percent: 100,
    booking_deadline: null as string | null,
    withdrawal_deadline: null as string | null,
    waitlist_offer_hours: 48,
    notification_application_submitted: false,
    notification_application_approved: false,
    notification_application_moved_to_waitlist: false,
    notification_waitlist_offer_sent: true,
    notification_sick_note_submitted: false,
    notification_sick_note_status: false,
    notification_withdrawal_approved: false,
    notification_template_application_submitted: null as string | null,
    notification_template_application_approved: null as string | null,
    notification_template_application_moved_to_waitlist: null as string | null,
    notification_template_waitlist_offer_sent: null as string | null,
    notification_template_sick_note_submitted: null as string | null,
    notification_template_sick_note_status: null as string | null,
    peoplevol_portal_url: 'https://people.roskilde-festival.dk/volunteer',
    mailchimp_api_key: '',
    mailchimp_audience_id: '',
    mailchimp_server_prefix: '',
    mailchimp_sync_tag: '',
    notification_template_withdrawal_approved: null as string | null,
    notification_template_shifts_updated: null as string | null,
  });
  const [constraintForm, setConstraintForm] = useState({
    name: '',
    description: '',
    role_id: '' as number | '' ,
    tag_id: '' as number | '' ,
    min_shifts: 1,
    max_shifts: 1,
  });

  const [shiftForm, setShiftForm] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    location: '',
    tags: [] as number[],
    role_requirements: [{ role_name: 'volunteer', max_count: 10 }] as Array<{ role_name: string; max_count: number }>,
    manual_full: false,
  });
  const [editingShiftId, setEditingShiftId] = useState<number | null>(null);
  const [bulkShiftForm, setBulkShiftForm] = useState({
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    title: '',
    description: '',
    location: '',
    tags: [] as number[],
    role_requirements: [{ role_name: 'volunteer', max_count: 10 }] as Array<{ role_name: string; max_count: number }>,
  });
  const [showBulkCreate, setShowBulkCreate] = useState(false);
  const [shiftPlanImportLoading, setShiftPlanImportLoading] = useState(false);
  const [shiftPlanExportLoading, setShiftPlanExportLoading] = useState(false);
  const [approvingApplicationId, setApprovingApplicationId] = useState<number | null>(null);
  const [approvingRole, setApprovingRole] = useState('volunteer');
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [settingsSubTab, setSettingsSubTab] = useState<'general' | 'volunteer' | 'notifications' | 'planning' | 'danger'>('general');
  const [mailchimpSyncing, setMailchimpSyncing] = useState(false);
  const [mailchimpDryRun, setMailchimpDryRun] = useState(true);
  const [mailchimpSyncResult, setMailchimpSyncResult] = useState<{ total: number; tag?: string; dry_run?: boolean; synced: number; failed: number; removed?: number; removal_failed?: number; would_sync?: number; would_remove?: number } | null>(null);
  const [authLogs, setAuthLogs] = useState<Array<{ id: number; event_type: string; email?: string | null; ip_address?: string | null; details?: any; created_at: string }>>([]);
  const [authLogTotalStorageBytes, setAuthLogTotalStorageBytes] = useState(0);
  const [authLogsLoading, setAuthLogsLoading] = useState(false);
  const [authLogTypeFilter, setAuthLogTypeFilter] = useState('');
  const [authLogEmailFilter, setAuthLogEmailFilter] = useState('');
  const [authLogFromFilter, setAuthLogFromFilter] = useState(() => getDefaultLogDateRange().from);
  const [authLogToFilter, setAuthLogToFilter] = useState(() => getDefaultLogDateRange().to);
  const knownAuthLogTypes = [
    'login_link_requested',
    'login_link_sent',
    'login_link_issued_dev_bypass',
    'login_link_request_missing_email',
    'login_link_request_failed',
    'login_validate_missing_token',
    'login_success',
    'login_failed',
    'volunteer_shift_booked',
    'volunteer_shift_booked_bulk',
    'volunteer_shift_booking_cancelled',
    'volunteer_withdrawal_requested',
    'volunteer_withdrawal_cancelled',
    'volunteer_sick_note_submitted',
    'volunteer_sick_note_cancelled',
    'supervisor_view_shifts',
    'supervisor_view_shift_volunteers',
    'supervisor_update_attendance',
    'supervisor_update_volunteer_notes',
    'supervisor_update_shift_notes',
    'admin_assign_volunteer',
    'admin_reassign_volunteer',
    'admin_unassign_volunteer',
    'email_sent',
    'email_send_failed',
    'email_send_failed_no_transporter',
    'email_send_suppressed_dev_bypass',
    'email_send_skipped_missing_recipient',
  ];
  const authLogTypeOptions = Array.from(new Set([...knownAuthLogTypes, ...authLogs.map((log) => log.event_type)])).sort();
  const [showNotificationTemplates, setShowNotificationTemplates] = useState({
    application_submitted: false,
    application_approved: false,
    application_moved_to_waitlist: false,
    waitlist_offer_sent: false,
    sick_note_submitted: false,
    sick_note_status: false,
    withdrawal_approved: false,
    shifts_updated: false,
  });
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [showFinalDeleteConfirm, setShowFinalDeleteConfirm] = useState(false);
  const [showDeleteNonAdminModal, setShowDeleteNonAdminModal] = useState(false);
  const [deleteNonAdminConfirmText, setDeleteNonAdminConfirmText] = useState('');
  const [showFinalDeleteNonAdminConfirm, setShowFinalDeleteNonAdminConfirm] = useState(false);
  const [showRoleSelectionModal, setShowRoleSelectionModal] = useState(false);
  const [roleSelectionOptions, setRoleSelectionOptions] = useState<string[]>([]);
  const [selectedAssignRole, setSelectedAssignRole] = useState<string>('');
  const [pendingAssignments, setPendingAssignments] = useState<{userId: number, shiftId: number}[]>([]);
  const [showHandledDeviations, setShowHandledDeviations] = useState(false);
  const [mode, setMode] = useState<'dashboard' | 'hr' | 'duty-manager' | 'admin'>(() => {
    const savedMode = localStorage.getItem('adminMode');
    return (savedMode === 'dashboard' || savedMode === 'hr' || savedMode === 'duty-manager' || savedMode === 'admin') ? savedMode : 'admin';
  });
  const [editingShiftNotes, setEditingShiftNotes] = useState<{ [key: number]: string }>({});
  const [expandedShifts, setExpandedShifts] = useState<Set<number>>(new Set());
  const [sickAdminNotes, setSickAdminNotes] = useState<Record<number, string>>({});
  const sickNotesSaveTimeouts = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const handleModeChange = (newMode: 'dashboard' | 'hr' | 'duty-manager' | 'admin') => {
    setMode(newMode);
    localStorage.setItem('adminMode', newMode);
    // Reset to appropriate default tab for the mode
    if (newMode === 'hr') handleTabChange('applications');
    else if (newMode === 'duty-manager') handleTabChange('assignments');
    else if (newMode === 'dashboard') handleTabChange('overview');
    else handleTabChange('shifts');
  };

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate('/login');
      return;
    }

    if (user.role !== 'admin') {
      navigate('/');
      return;
    }

    loadAll();
  }, [authLoading, user, navigate]);

  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [usersRes, rolesRes, constraintsRes, tagsRes, bookingStatsRes, shiftStatsRes, usersByRoleRes, applicationsRes, shiftsRes, settingsRes, assignmentsRes, deviationsRes, sickNotesRes, withdrawalsRes, waitlistRes, newsRes, todosRes] =
        await Promise.all([
          adminService.getUsers(),
          adminService.getRoles(),
          adminService.getConstraints(),
          shiftsService.getTags(),
          adminService.getBookingStats(),
          adminService.getShiftStats(),
          adminService.getUsersByRole(),
          volunteerApplicationService.getPendingApplications(),
          shiftsService.getAllShifts(),
          adminService.getGlobalSettings(),
          adminService.getAssignments(),
          adminService.getDeviations(),
          adminService.getSickNotes(),
          adminWithdrawalService.getAll(),
          adminService.getWaitlist(),
          adminService.getNewsPosts(),
          adminService.getTodos(),
        ]);

      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setConstraints(constraintsRes.data);
      setTags(tagsRes.data);
      setBookingStats(bookingStatsRes.data);
      setShiftStats(shiftStatsRes.data);
      setUsersByRole(usersByRoleRes.data);
      setApplications(applicationsRes.data);
      setShifts(shiftsRes.data);
      setSickNotes(sickNotesRes.data || []);
      // Sort deviations by shift start_time, oldest first
      const sortedDeviations = (deviationsRes.data || []).slice().sort((a: any, b: any) => {
        const aStart = new Date(a.start_time).getTime() || 0;
        const bStart = new Date(b.start_time).getTime() || 0;
        return aStart - bStart;
      });
      setDeviations(sortedDeviations);
      setWithdrawalRequests(withdrawalsRes.data || []);
      setWaitlistUsers(waitlistRes.data || []);
      setNewsPosts(newsRes.data || []);
      setTodos(todosRes.data || []);
      setGlobalSettings({
        event_name: settingsRes.data?.event_name ?? 'Roskilde Festival',
        min_hours_between_shifts: settingsRes.data?.min_hours_between_shifts ?? 0,
        show_shift_title: settingsRes.data?.show_shift_title ?? true,
        show_shift_location: settingsRes.data?.show_shift_location ?? true,
        show_next_shift_box: settingsRes.data?.show_next_shift_box ?? true,
        show_available_spots: settingsRes.data?.show_available_spots ?? true,
        show_waitlist_position: settingsRes.data?.show_waitlist_position ?? true,
        volunteer_login_waitlist_modal_enabled: settingsRes.data?.volunteer_login_waitlist_modal_enabled ?? false,
        volunteer_login_new_signups_closed: settingsRes.data?.volunteer_login_new_signups_closed ?? false,
        whatsapp_group_join_url: settingsRes.data?.whatsapp_group_join_url ?? '',
        whatsapp_group_confirmation_code: settingsRes.data?.whatsapp_group_confirmation_code ?? '',
        booking_enabled: settingsRes.data?.booking_enabled ?? 'booking-enabled',
        sick_note_enabled: settingsRes.data?.sick_note_enabled ?? false,
        withdrawal_permitted: settingsRes.data?.withdrawal_permitted ?? true,
        fill_threshold_critical: settingsRes.data?.fill_threshold_critical ?? 30,
        fill_threshold_warning: settingsRes.data?.fill_threshold_warning ?? 50,
        fill_threshold_low: settingsRes.data?.fill_threshold_low ?? 80,
        booking_capacity_percent: settingsRes.data?.booking_capacity_percent ?? 100,
        booking_deadline: settingsRes.data?.booking_deadline ?? null,
        withdrawal_deadline: settingsRes.data?.withdrawal_deadline ?? null,
        waitlist_offer_hours: settingsRes.data?.waitlist_offer_hours ?? 48,
        notification_application_submitted: settingsRes.data?.notification_application_submitted ?? false,
        notification_application_approved: settingsRes.data?.notification_application_approved ?? false,
        notification_application_moved_to_waitlist: settingsRes.data?.notification_application_moved_to_waitlist ?? false,
        notification_waitlist_offer_sent: settingsRes.data?.notification_waitlist_offer_sent ?? true,
        notification_sick_note_submitted: settingsRes.data?.notification_sick_note_submitted ?? false,
        notification_sick_note_status: settingsRes.data?.notification_sick_note_status ?? false,
        notification_withdrawal_approved: settingsRes.data?.notification_withdrawal_approved ?? false,
        notification_template_application_submitted: settingsRes.data?.notification_template_application_submitted ?? null,
        notification_template_application_approved: settingsRes.data?.notification_template_application_approved ?? null,
        notification_template_application_moved_to_waitlist: settingsRes.data?.notification_template_application_moved_to_waitlist ?? null,
        notification_template_waitlist_offer_sent: settingsRes.data?.notification_template_waitlist_offer_sent ?? null,
        notification_template_sick_note_submitted: settingsRes.data?.notification_template_sick_note_submitted ?? null,
        notification_template_sick_note_status: settingsRes.data?.notification_template_sick_note_status ?? null,
        notification_template_withdrawal_approved: settingsRes.data?.notification_template_withdrawal_approved ?? null,
        notification_template_shifts_updated: settingsRes.data?.notification_template_shifts_updated ?? null,
        peoplevol_portal_url: settingsRes.data?.peoplevol_portal_url ?? 'https://people.roskilde-festival.dk/volunteer',
        mailchimp_api_key: settingsRes.data?.mailchimp_api_key ?? '',
        mailchimp_audience_id: settingsRes.data?.mailchimp_audience_id ?? '',
        mailchimp_server_prefix: settingsRes.data?.mailchimp_server_prefix ?? '',
        mailchimp_sync_tag: settingsRes.data?.mailchimp_sync_tag ?? '',
      });
      // Build mapping of shift_id -> volunteers array
      const byShift: Record<number, any[]> = {};
      (assignmentsRes.data || []).forEach((row: any) => {
        byShift[row.shift_id] = row.volunteers || [];
      });
      setAssignmentsByShift(byShift);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const openReassignModal = (bookingId: number, context: 'default' | 'sick-note' = 'default', originalShiftId?: number) => {
    setReassignContext(context);
    setReassignBookingId(bookingId);
    setReassignTargetShiftId('');
    setReassignOriginalShiftId(originalShiftId || null);
    setShowReassignModal(true);
  };

  const closeReassignModal = () => {
    setShowReassignModal(false);
    setReassignBookingId(null);
    setReassignTargetShiftId('');
    setReassignContext('default');
    setReassignOriginalShiftId(null);
    setPendingSickNoteId(null);
    setPendingSickNoteAdminNotes('');
    setPendingSickRequireDoctor(undefined);
  };

  const confirmReassign = async () => {
    if (!reassignBookingId || reassignTargetShiftId === '' || isNaN(Number(reassignTargetShiftId))) return;
    try {
      // If updating a sick note, do that FIRST before reassigning
      if (pendingSickNoteId) {
        await handleUpdateSickNote(pendingSickNoteId, 'reschedule_accepted', pendingSickRequireDoctor, pendingSickNoteAdminNotes, Number(reassignTargetShiftId));
      }
      
      // Then reassign the volunteer
      const resp = await adminService.reassignVolunteer({ booking_id: reassignBookingId, to_shift_id: Number(reassignTargetShiftId) });
      const [shiftsRes, assignmentsRes, sickNotesRes] = await Promise.all([
        shiftsService.getAllShifts(),
        adminService.getAssignments(),
        adminService.getSickNotes(),
      ]);
      setShifts(shiftsRes.data);
      const byShift: Record<number, any[]> = {};
      (assignmentsRes.data || []).forEach((row: any) => {
        byShift[row.shift_id] = row.volunteers || [];
      });
      setAssignmentsByShift(byShift);
      setSickNotes(sickNotesRes.data || []);
      closeReassignModal();
      const w = (resp.data?.warnings || []) as string[];
      if (w.length) {
        alert(`Reassigned with warnings:\n- ${w.join('\n- ')}`);
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to reassign');
    }
  };

  const openAddModal = (shiftId: number) => {
    setAddTargetShiftId(shiftId);
    setSelectedUserIds([]);
    setUserSearch('');
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setAddTargetShiftId(null);
    setSelectedUserIds([]);
    setUserSearch('');
  };

  const toggleUserSelection = (userId: number) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const confirmAddVolunteers = async () => {
    if (!addTargetShiftId || selectedUserIds.length === 0) return;
    
    // Check if role selection is needed
    const shift = shifts.find(s => s.id === addTargetShiftId);
    if (!shift || !shift.role_requirements || shift.role_requirements.length === 0) {
      // No role requirements, proceed normally
      await executeAssignments(selectedUserIds, addTargetShiftId, null);
      return;
    }
    
    // Check each user to see if they need role selection
    const needsRoleSelection = selectedUserIds.some(uid => {
      const user = users.find(u => u.id === uid);
      if (!user) return false;
      const eligibleRoles = getEligibleRoles(user.role, shift.role_requirements!);
      return eligibleRoles.length > 1;
    });
    
    if (needsRoleSelection) {
      // At least one user has multiple role options
      // For simplicity, we'll ask for all users
      const firstUser = users.find(u => u.id === selectedUserIds[0]);
      if (firstUser) {
        const eligibleRoles = getEligibleRoles(firstUser.role, shift.role_requirements!);
        if (eligibleRoles.length > 1) {
          setRoleSelectionOptions(eligibleRoles);
          setSelectedAssignRole(eligibleRoles[0]);
          setPendingAssignments(selectedUserIds.map(uid => ({ userId: uid, shiftId: addTargetShiftId })));
          setShowRoleSelectionModal(true);
          return;
        }
      }
    }
    
    // No role selection needed, proceed
    await executeAssignments(selectedUserIds, addTargetShiftId, null);
  };
  
  const getEligibleRoles = (userRole: string, roleRequirements: ShiftRoleRequirement[]): string[] => {
    const roleHierarchy: Record<string, number> = { 'volunteer': 1, 'supervisor': 2, 'admin': 3 };
    const userLevel = roleHierarchy[userRole] || 1;
    
    return roleRequirements
      .filter(req => {
        const reqLevel = roleHierarchy[req.role_name] || 1;
        return reqLevel <= userLevel;
      })
      .map(req => req.role_name);
  };
  
  const executeAssignments = async (userIds: number[], shiftId: number, assignedRole: string | null) => {
    try {
      const warningsCollected: string[] = [];
      for (const uid of userIds) {
        try {
          const payload: any = { user_id: uid, shift_id: shiftId };
          if (assignedRole) {
            payload.assigned_role = assignedRole;
          }
          const resp = await adminService.assignVolunteer(payload);
          const w = (resp.data?.warnings || []) as string[];
          if (w.length) warningsCollected.push(...w.map(msg => `User ${uid}: ${msg}`));
        } catch (err: any) {
          alert(err.response?.data?.error || `Failed to assign user ${uid}`);
        }
      }
      const [shiftsRes, assignmentsRes] = await Promise.all([
        shiftsService.getAllShifts(),
        adminService.getAssignments(),
      ]);
      setShifts(shiftsRes.data);
      const byShift: Record<number, any[]> = {};
      (assignmentsRes.data || []).forEach((row: any) => {
        byShift[row.shift_id] = row.volunteers || [];
      });
      setAssignmentsByShift(byShift);
      closeAddModal();
      if (warningsCollected.length) {
        alert(`Assignments completed with warnings:\n- ${warningsCollected.join('\n- ')}`);
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to complete assignment');
    }
  };
  
  const confirmRoleSelection = async () => {
    if (!selectedAssignRole || pendingAssignments.length === 0) return;
    setShowRoleSelectionModal(false);
    await executeAssignments(
      pendingAssignments.map(p => p.userId),
      pendingAssignments[0].shiftId,
      selectedAssignRole
    );
    setPendingAssignments([]);
    setSelectedAssignRole('');
    setRoleSelectionOptions([]);
  };

  const openSwapModal = () => {
    setShowSwapModal(true);
    setSwapVolunteer1Search('');
    setSwapVolunteer2Search('');
    setSwapVolunteer1Id(null);
    setSwapVolunteer2Id(null);
    setSwapBooking1Id(null);
    setSwapBooking2Id(null);
  };

  const closeSwapModal = () => {
    setShowSwapModal(false);
    setSwapVolunteer1Search('');
    setSwapVolunteer2Search('');
    setSwapVolunteer1Id(null);
    setSwapVolunteer2Id(null);
    setSwapBooking1Id(null);
    setSwapBooking2Id(null);
  };

  const handleSwapAssignments = async () => {
    if (!swapBooking1Id || !swapBooking2Id) return;
    try {
      setSwappingAssignments(true);
      await adminService.swapVolunteerAssignments({ booking_id_1: swapBooking1Id, booking_id_2: swapBooking2Id });
      await loadAll();
      closeSwapModal();
      alert('Shift swap completed successfully');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to swap shifts');
    } finally {
      setSwappingAssignments(false);
    }
  };

  const handleUnassignVolunteer = async (bookingId: number) => {
    if (!confirm('Unassign this volunteer from the shift?')) return;
    try {
      await adminService.unassignVolunteer(bookingId);
      const promises: any[] = [
        shiftsService.getAllShifts(),
        adminService.getAssignments(),
      ];
      if (modalUserId) promises.push(adminService.getUserBookingsAdmin(modalUserId));
      const results = await Promise.all(promises);
      const shiftsRes = results[0];
      const assignmentsRes = results[1];
      const userBookingsRes = results[2];
      setShifts(shiftsRes.data);
      const byShift: Record<number, any[]> = {};
      (assignmentsRes.data || []).forEach((row: any) => {
        byShift[row.shift_id] = row.volunteers || [];
      });
      setAssignmentsByShift(byShift);
      if (userBookingsRes && userBookingsRes.data) setModalUserShifts(userBookingsRes.data);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to unassign');
    }
  };

  const handleMoveWaitlistPosition = async (user: User) => {
    const rawValue = waitlistMoveTargets[user.id] ?? String(user.display_position || user.waiting_list_position || '');
    const targetPosition = Number(rawValue);

    if (!Number.isInteger(targetPosition) || targetPosition < 1) {
      alert('Please enter a valid position (1 or higher).');
      return;
    }

    try {
      setMovingWaitlistUserId(user.id);
      await adminService.moveWaitlistPosition(user.id, targetPosition);
      await loadAll();
      setWaitlistMoveTargets((prev) => {
        const next = { ...prev };
        delete next[user.id];
        return next;
      });
      setOpenWaitlistMoveUserId(null);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to move waitlist user');
    } finally {
      setMovingWaitlistUserId(null);
    }
  };

  const handleSendBatchInvites = async () => {
    if (selectedWaitlistUserIds.size < 2) {
      alert('Please select at least 2 volunteers for batch invite.');
      return;
    }

    try {
      setSendingBatchOffers(true);
      const userIds = Array.from(selectedWaitlistUserIds);
      await adminService.sendBatchWaitlistOffers(userIds);
      setSelectedWaitlistUserIds(new Set());
      setShowBatchInviteConfirm(false);
      await loadAll();
      alert(`Batch invites sent to ${userIds.length} volunteers! First to accept gets the spot.`);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to send batch invites');
    } finally {
      setSendingBatchOffers(false);
    }
  };

  const openUserShiftsModal = async (userId: number, displayName: string) => {
    setModalUserId(userId);
    setModalUserName(displayName);
    setShowUserShiftsModal(true);
    try {
      const res = await adminService.getUserBookingsAdmin(userId);
      setModalUserShifts(res.data);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to load user shifts');
    }
  };

  const closeUserShiftsModal = () => {
    setShowUserShiftsModal(false);
    setModalUserId(null);
    setModalUserName('');
    setModalUserShifts([]);
  };

  const openApproveWithdrawalModal = (request: WithdrawalRequest) => {
    setApproveWithdrawalTarget(request);
    setApproveWithdrawalAdminNotes('');
    // Always start unchecked; if future shifts exist, user must explicitly confirm deletion.
    setApproveWithdrawalDeleteShifts(false);
    setShowApproveWithdrawalModal(true);
  };

  const closeApproveWithdrawalModal = () => {
    setShowApproveWithdrawalModal(false);
    setApproveWithdrawalTarget(null);
    setApproveWithdrawalAdminNotes('');
    setApproveWithdrawalDeleteShifts(false);
  };

  const confirmApproveWithdrawal = async () => {
    if (!approveWithdrawalTarget) return;

    const futureShiftsCount = Number(approveWithdrawalTarget.future_shifts_count || 0);
    if (futureShiftsCount > 0 && !approveWithdrawalDeleteShifts) {
      alert('This volunteer has future shifts. You must check "Delete all future shifts" before approving withdrawal.');
      return;
    }

    try {
      setApprovingWithdrawalId(approveWithdrawalTarget.id);
      await adminWithdrawalService.update(approveWithdrawalTarget.id, {
        status: 'approved',
        admin_notes: approveWithdrawalAdminNotes.trim() || undefined,
        delete_shifts: approveWithdrawalDeleteShifts,
      });
      closeApproveWithdrawalModal();
      await loadAll();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to approve withdrawal');
    } finally {
      setApprovingWithdrawalId(null);
    }
  };

  const handleUserRoleChange = async (id: number, role: User['role']) => {
    await adminService.updateUserRole(id, role);
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, role } : u)));
  };

  const handleUserImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setUserImportText(String(reader.result || ''));
    };
    reader.readAsText(file);
  };

  const handleImportUsers = async () => {
    if (!userImportText.trim()) {
      setUserImportError('Please provide CSV content before importing.');
      return;
    }
    setUserImportError('');
    setUserImportResult(null);
    setUserImportLoading(true);
    try {
      const res = await adminService.importUsers({ csvText: userImportText });
      setUserImportResult(res.data);
      const usersRes = await adminService.getUsers();
      setUsers(usersRes.data);
    } catch (err: any) {
      setUserImportError(err.response?.data?.error || 'Failed to import users');
    } finally {
      setUserImportLoading(false);
    }
  };

  const handleWaitlistImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setWaitlistImportText(String(reader.result || ''));
    };
    reader.readAsText(file);
  };

  const handleImportWaitlist = async () => {
    if (!waitlistImportText.trim()) {
      setWaitlistImportError('Please provide CSV content before importing.');
      return;
    }
    setWaitlistImportError('');
    setWaitlistImportResult(null);
    setWaitlistImportLoading(true);
    try {
      const res = await adminService.importWaitlistUsers({ csvText: waitlistImportText });
      setWaitlistImportResult(res.data);
      await loadAll();
    } catch (err: any) {
      setWaitlistImportError(err.response?.data?.error || 'Failed to import waitlist users');
    } finally {
      setWaitlistImportLoading(false);
    }
  };

  const handleDeleteUser = async (id: number) => {
    const user = users.find(u => u.id === id);
    
    // If user is approved, trigger withdrawal instead of deletion
    if (user?.application_status === 'approved') {
      handleWithdrawUser(id);
      return;
    }
    
    if (!confirm('Delete this user and their bookings?')) return;
    await adminService.deleteUser(id);
    setUsers((prev) => prev.filter((u) => u.id !== id));
  };

  const handleExportShiftPlan = async () => {
    try {
      setShiftPlanExportLoading(true);
      const res = await shiftsService.exportShiftPlan();
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      a.href = url;
      a.download = `shift-plan-${timestamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to export shift plan');
    } finally {
      setShiftPlanExportLoading(false);
    }
  };

  const handleImportShiftPlanFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setShiftPlanImportLoading(true);
      const text = await file.text();
      const parsed = JSON.parse(text);

      const shiftsToImport = Array.isArray(parsed)
        ? parsed
        : (Array.isArray(parsed?.shifts) ? parsed.shifts : null);

      if (!Array.isArray(shiftsToImport) || shiftsToImport.length === 0) {
        alert('Invalid file format. Expected JSON with a non-empty shifts array.');
        return;
      }

      const res = await shiftsService.importShiftPlan({
        shifts: shiftsToImport,
        create_missing_tags: true,
      });

      await loadAll();
      const summary = res.data;
      const errorPreview = summary.errors?.length ? `\n\nErrors:\n${summary.errors.slice(0, 5).join('\n')}` : '';
      alert(
        `Import finished.\nCreated: ${summary.created}\nUpdated: ${summary.updated || 0}\nSkipped: ${summary.skipped}\nCreated tags: ${summary.created_tags}${errorPreview}`
      );
    } catch (err: any) {
      alert(err.response?.data?.error || err.message || 'Failed to import shift plan');
    } finally {
      setShiftPlanImportLoading(false);
      e.target.value = '';
    }
  };

  const handleWithdrawUser = async (id: number) => {
    const user = users.find(u => u.id === id);
    if (!user) return;
    
    try {
      // Get user's shifts
      const shiftsRes = await adminService.getUserShifts(id);
      setWithdrawingUserId(id);
      setWithdrawingUserName(user.full_name || user.name || user.email);
      setWithdrawingUserShifts(shiftsRes.data || []);
      setShowUserWithdrawalModal(true);
    } catch (err) {
      alert('Failed to load user shifts');
    }
  };

  const confirmWithdrawUser = async () => {
    if (!withdrawingUserId) return;
    
    try {
      // Update user status to withdrawn
      await adminService.updateUserApplicationStatus(withdrawingUserId, 'withdrawn');
      setUsers((prev) => prev.map((u) => 
        u.id === withdrawingUserId ? { ...u, withdrawn: true, application_status: 'withdrawn' } : u
      ));
      setShowUserWithdrawalModal(false);
      setWithdrawingUserId(null);
      setWithdrawingUserName('');
      setWithdrawingUserShifts([]);
    } catch (err) {
      alert('Failed to withdraw user');
    }
  };

  const handleCreateTag = async () => {
    if (!newTag.name.trim()) return;
    await adminService.createTag(newTag);
    const tagsRes = await shiftsService.getTags();
    setTags(tagsRes.data);
    setNewTag({ name: '', description: '' });
  };

  const handleDeleteTag = async (id: number) => {
    if (!confirm('Delete this tag?')) return;
    await adminService.deleteTag(id);
    setTags((prev) => prev.filter((t) => t.id !== id));
  };

  const handleCreateOrUpdateNewsPost = async () => {
    if (!newsForm.title.trim() || !newsForm.content.trim()) {
      alert('Title and content are required');
      return;
    }
    try {
      if (editingNewsId) {
        const response = await adminService.updateNewsPost(editingNewsId, {
          title: newsForm.title,
          content: newsForm.content,
          is_published: newsForm.is_published,
        });
        setNewsPosts((prev) => prev.map((post) => (post.id === editingNewsId ? response.data : post)));
      } else {
        const response = await adminService.createNewsPost({
          title: newsForm.title,
          content: newsForm.content,
          is_published: newsForm.is_published,
        });
        setNewsPosts((prev) => [response.data, ...prev]);
      }

      setNewsForm({ title: '', content: '', is_published: true });
      setEditingNewsId(null);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save news post');
    }
  };

  const handleEditNewsPost = (post: NewsPost) => {
    setEditingNewsId(post.id);
    setNewsForm({
      title: post.title,
      content: post.content,
      is_published: post.is_published !== false,
    });
  };

  const handleCancelNewsEdit = () => {
    setEditingNewsId(null);
    setNewsForm({ title: '', content: '', is_published: true });
  };

  const handleAddTodo = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = todoInput.trim();
    if (!text) return;
    try {
      const res = await adminService.createTodo(text);
      setTodos((prev) => [...prev, res.data]);
      setTodoInput('');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to add todo');
    }
  };

  const handleDeleteTodo = async (id: number) => {
    try {
      await adminService.deleteTodo(id);
      setTodos((prev) => prev.filter((t) => t.id !== id));
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to remove todo');
    }
  };

  const handleDeleteNewsPost = async (id: number) => {
    if (!confirm('Delete this post?')) return;
    try {
      await adminService.deleteNewsPost(id);
      setNewsPosts((prev) => prev.filter((post) => post.id !== id));
      if (editingNewsId === id) {
        handleCancelNewsEdit();
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete news post');
    }
  };

  const handleCreateConstraint = async () => {
    if (!constraintForm.name.trim()) return;

    const payload = {
      ...constraintForm,
      type: 'limit',
      role_id: constraintForm.role_id === '' ? undefined : Number(constraintForm.role_id),
      tag_id: constraintForm.tag_id === '' ? undefined : Number(constraintForm.tag_id),
      min_shifts: Number(constraintForm.min_shifts),
      max_shifts: Number(constraintForm.max_shifts),
    };

    await adminService.createConstraint(payload);
    const constraintsRes = await adminService.getConstraints();
    setConstraints(constraintsRes.data);
    setConstraintForm({ name: '', description: '', role_id: '', tag_id: '', min_shifts: 1, max_shifts: 1 });
  };

  const handleSaveGlobalSettings = async () => {
    try {
      // Store globally by updating a system constraint or in backend settings
      await adminService.updateGlobalSettings(globalSettings);
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save settings');
    }
  };

  const handleSyncMailchimpActive = async () => {
    setMailchimpSyncing(true);
    setMailchimpSyncResult(null);
    try {
      const response = await adminService.syncMailchimpActive({ dry_run: mailchimpDryRun });
      setMailchimpSyncResult({
        total: response.data.total || 0,
        tag: response.data.tag || '',
        dry_run: response.data.dry_run === true,
        synced: response.data.synced || 0,
        failed: response.data.failed || 0,
        removed: response.data.removed || 0,
        removal_failed: response.data.removal_failed || 0,
        would_sync: response.data.would_sync || 0,
        would_remove: response.data.would_remove || 0,
      });
      if ((response.data.failed || 0) > 0 || (response.data.removal_failed || 0) > 0) {
        alert(`MailChimp sync completed with errors. Add failures: ${response.data.failed || 0}, remove failures: ${response.data.removal_failed || 0}. Open browser devtools/network response for details.`);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to sync MailChimp audience');
    } finally {
      setMailchimpSyncing(false);
    }
  };

  const refreshAuthLogs = async (overrides?: { eventType?: string; email?: string; from?: string; to?: string }) => {
    const effectiveType = overrides?.eventType ?? authLogTypeFilter;
    const effectiveEmail = overrides?.email !== undefined ? overrides.email : authLogEmailFilter;
    const effectiveFrom = overrides?.from ?? authLogFromFilter;
    const effectiveTo = overrides?.to ?? authLogToFilter;
    setAuthLogsLoading(true);
    try {
      const response = await adminService.getAuthLogs({
        limit: 400,
        event_type: effectiveType || undefined,
        email: effectiveEmail || undefined,
        from: effectiveFrom ? `${effectiveFrom}T00:00:00Z` : undefined,
        to: effectiveTo ? `${effectiveTo}T23:59:59Z` : undefined,
      });
      setAuthLogs(response.data?.logs || []);
      setAuthLogTotalStorageBytes(Number(response.data?.total_storage_bytes || 0));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load auth logs');
    } finally {
      setAuthLogsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'logs') {
      refreshAuthLogs();
    }
  }, [activeTab]);

  const handleDeleteAllShifts = async () => {
    try {
      await adminService.deleteAllShifts();
      setShowFinalDeleteConfirm(false);
      setShowDeleteAllModal(false);
      setDeleteConfirmText('');
      // Reload shifts
      const response = await shiftsService.getAllShifts();
      setShifts(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete shifts');
    }
  };

  const handleDeleteInactiveUsers = async () => {
    if (!confirm('Delete all inactive/guest users? This cannot be undone.')) return;

    try {
      const response = await adminService.deleteInactiveUsers();
      const usersRes = await adminService.getUsers();
      setUsers(usersRes.data);
      alert(`Deleted ${response.data?.deletedCount ?? 0} users`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete inactive users');
    }
  };

  const handleDeleteNonAdminUsers = async () => {
    try {
      const response = await adminService.deleteNonAdminUsers();
      const usersRes = await adminService.getUsers();
      setUsers(usersRes.data);
      alert(`Deleted ${response.data?.deletedCount ?? 0} users`);
      setShowFinalDeleteNonAdminConfirm(false);
      setShowDeleteNonAdminModal(false);
      setDeleteNonAdminConfirmText('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete users');
    }
  };

  const handleToggleConstraint = async (id: number, enabled: boolean) => {
    const constraint = constraints.find((c) => c.id === id);
    if (!constraint) return;
    
    await adminService.updateConstraint(id, {
      name: constraint.name,
      description: constraint.description,
      min_shifts: constraint.min_shifts,
      max_shifts: constraint.max_shifts,
      enabled,
    });
    setConstraints((prev) => prev.map((c) => (c.id === id ? { ...c, enabled } : c)));
  };

  const handleDeleteConstraint = async (id: number) => {
    if (!confirm('Delete this constraint?')) return;
    await adminService.deleteConstraint(id);
    setConstraints((prev) => prev.filter((c) => c.id !== id));
  };

  const handleApproveApplication = async (applicationId: number) => {
    await volunteerApplicationService.approveApplication(applicationId, approvingRole);
    setApplications((prev) => prev.filter((a) => a.id !== applicationId));
    setApprovingApplicationId(null);
    setApprovingRole('volunteer');
  };

  const handleRejectApplication = async (applicationId: number) => {
    const notes = prompt('Enter rejection reason (optional):');
    await volunteerApplicationService.rejectApplication(applicationId, notes || undefined);
    setApplications((prev) => prev.filter((a) => a.id !== applicationId));
  };

  const handleCreateOrUpdateShift = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (shiftForm.start_time && shiftForm.end_time && shiftForm.end_time <= shiftForm.start_time) {
        alert('End time must be after start time');
        return;
      }
      const dataToSend = {
        title: shiftForm.title,
        description: shiftForm.description,
        start_time: `${shiftForm.start_time}:00`,
        end_time: `${shiftForm.end_time}:00`,
        location: shiftForm.location,
        tags: shiftForm.tags,
        role_requirements: shiftForm.role_requirements,
        manual_full: !!shiftForm.manual_full,
      };

      if (editingShiftId) {
        await shiftsService.updateShift(editingShiftId, dataToSend);
      } else {
        await shiftsService.createShift(dataToSend);
      }
      const shiftsRes = await shiftsService.getAllShifts();
      setShifts(shiftsRes.data);
      setShiftForm({
        title: '',
        description: '',
        start_time: '',
        end_time: '',
        location: '',
        tags: [],
        role_requirements: [{ role_name: 'volunteer', max_count: 10 }],
        manual_full: false,
      });
      setEditingShiftId(null);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save shift');
    }
  };

  const handleDeleteShift = async (id: number) => {
    if (!confirm('Delete this shift?')) return;
    await shiftsService.deleteShift(id);
    setShifts((prev) => prev.filter((s) => s.id !== id));
  };

  const handleEditShift = (shift: any) => {
    setShiftForm({
      title: shift.title,
      description: shift.description || '',
      start_time: shift.start_time.substring(0, 16),
      end_time: shift.end_time.substring(0, 16),
      location: shift.location || '',
      tags: (shift.tags || []).map((t: any) => t.id),
      role_requirements: (shift.role_requirements || []).map((r: any) => ({ role_name: r.role_name, max_count: r.max_count })),
      manual_full: !!shift.manual_full,
    });
    setEditingShiftId(shift.id);
  };

  const handleCancelEdit = () => {
    setEditingShiftId(null);
    setShiftForm({
      title: '',
      description: '',
      start_time: '',
      end_time: '',
      location: '',
      tags: [],
      role_requirements: [],
      manual_full: false,
    });
  };

  const handleBulkCreateShifts = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await shiftsService.bulkCreateShifts(bulkShiftForm);
      const shiftsRes = await shiftsService.getAllShifts();
      setShifts(shiftsRes.data);
      setBulkShiftForm({
        start_date: '',
        end_date: '',
        start_time: '',
        end_time: '',
        title: '',
        description: '',
        location: '',
        tags: [],
        role_requirements: [{ role_name: 'volunteer', max_count: 10 }],
      });
      setShowBulkCreate(false);
      alert(`Successfully created ${response.data.count} shifts`);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create bulk shifts');
    }
  };

  const handleMarkDeviation = async (bookingId: number, excused: boolean | null, handled: boolean) => {
    try {
      await adminService.updateDeviation(bookingId, {
        absence_excused: excused,
        absence_handled: handled,
      });
      const deviationsRes = await adminService.getDeviations();
      setDeviations(deviationsRes.data);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update deviation');
    }
  };

  const handleDeleteSickNote = async (id: number) => {
    if (!confirm('Are you sure you want to delete this sick note? This action cannot be undone.')) {
      return;
    }

    try {
      await adminService.deleteSickNote(id);
      const sickRes = await adminService.getSickNotes();
      setSickNotes(sickRes.data || []);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete sick note');
    }
  };

  const handleLeaveFestival = async (sickNoteId: number, userId: number, userName: string) => {
    try {
      // Fetch the volunteer's future shifts
      const shiftsRes = await adminService.getUserFutureShifts(userId);
      setWithdrawalShifts(shiftsRes.data || []);
      setWithdrawalSickNoteId(sickNoteId);
      setWithdrawalVolunteerName(userName);
      setShowWithdrawalModal(true);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to fetch volunteer shifts');
    }
  };

  const confirmWithdrawal = async () => {
    if (!withdrawalSickNoteId) return;

    try {
      const response = await adminService.leaveFestival(withdrawalSickNoteId);
      alert(`Success: ${response.data.message}. Removed ${response.data.deletedShifts} future shifts.`);
      setShowWithdrawalModal(false);
      setWithdrawalSickNoteId(null);
      setWithdrawalShifts([]);
      const sickRes = await adminService.getSickNotes();
      setSickNotes(sickRes.data || []);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to process leave festival request');
    }
  };

  const handleUpdateSickNote = async (id: number, status: SickNote['status'], requireDoctorFlag?: boolean, admin_notes?: string, new_shift_id?: number) => {
    try {
      await adminService.updateSickNote(id, { status, admin_notes, require_doctors_note: requireDoctorFlag, new_shift_id });
      const res = await adminService.getSickNotes();
      setSickNotes(res.data || []);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update sick note');
    }
  };

  const autoSaveSickNote = (noteId: number, adminNotes: string, note: SickNote) => {
    // Clear existing timeout for this note
    if (sickNotesSaveTimeouts.current[noteId]) {
      clearTimeout(sickNotesSaveTimeouts.current[noteId]);
    }

    // Set new timeout to save after 1 second of no typing
    sickNotesSaveTimeouts.current[noteId] = setTimeout(async () => {
      try {
        await handleUpdateSickNote(noteId, note.status, note.require_doctors_note, adminNotes);
      } catch (err) {
        console.error('Failed to auto-save admin notes:', err);
      }
    }, 1000);
  };

  const startSickNoteReschedule = (note: SickNote) => {
    setPendingSickNoteId(note.id);
    setPendingSickNoteAdminNotes(sickAdminNotes[note.id] ?? note.admin_notes ?? '');
    setPendingSickRequireDoctor(note.require_doctors_note);
    openReassignModal(note.booking_id, 'sick-note', note.original_shift_id);
  };

  const handleShiftNotesChange = (shiftId: number, notes: string) => {
    setEditingShiftNotes(prev => ({ ...prev, [shiftId]: notes }));
  };

  const handleSaveShiftNotes = async (shiftId: number) => {
    try {
      const notes = editingShiftNotes[shiftId] || '';
      await supervisorService.updateShiftNotes(shiftId, notes);
      setShifts(prev => prev.map(s => s.id === shiftId ? { ...s, supervisor_notes: notes } : s));
      const newEditingNotes = { ...editingShiftNotes };
      delete newEditingNotes[shiftId];
      setEditingShiftNotes(newEditingNotes);
      alert('Shift notes saved');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save shift notes');
    }
  };

  const toggleShiftExpanded = (shiftId: number) => {
    setExpandedShifts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(shiftId)) {
        newSet.delete(shiftId);
      } else {
        newSet.add(shiftId);
      }
      return newSet;
    });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Loading admin dashboard...</p>
      </div>
    );
  }

  if (!user || user.role !== 'admin') return null;

  const parseNumber = (value?: number | string) => (value === undefined ? 0 : Number(value));
  const formatPhone = (value?: string | null) => {
    if (!value) return '—';
    const parsed = parsePhoneNumberFromString(value);
    if (parsed) {
      return parsed.formatInternational();
    }
    return value;
  };
  const swapCandidates = buildSwapCandidates(assignmentsByShift, shifts);
  const getUserDisplayName = (userId: number | undefined, fallbackName?: string, fallbackEmail?: string) => {
    if (!userId) return fallbackName || fallbackEmail || 'Volunteer';
    const found = users.find((u) => u.id === Number(userId));
    return (found && (found.full_name || found.name)) || fallbackName || fallbackEmail || 'Volunteer';
  };
  const selectedSwapVolunteer1 = swapCandidates.find((candidate) => candidate.id === swapVolunteer1Id) || null;
  const selectedSwapVolunteer2 = swapCandidates.find((candidate) => candidate.id === swapVolunteer2Id) || null;
  const selectedSwapBooking1 = selectedSwapVolunteer1?.bookings.find((booking: any) => booking.booking_id === swapBooking1Id) || null;
  const selectedSwapBooking2 = selectedSwapVolunteer2?.bookings.find((booking: any) => booking.booking_id === swapBooking2Id) || null;
  const filteredSwapCandidates1 = swapCandidates.filter((candidate) =>
    `${candidate.name} ${candidate.email}`.toLowerCase().includes(swapVolunteer1Search.toLowerCase())
  );
  const filteredSwapCandidates2 = swapCandidates.filter((candidate) =>
    candidate.id !== swapVolunteer1Id &&
    `${candidate.name} ${candidate.email}`.toLowerCase().includes(swapVolunteer2Search.toLowerCase())
  );
  const inactiveGuestCount = users.filter((u) =>
    u.role === 'guest' || u.withdrawn || u.application_status === 'rejected'
  ).length;
  const nonAdminCount = users.filter((u) => u.role !== 'admin').length;

  return (
    <>
      <Header user={user} onLogout={logout} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && <div className="bg-red-100 text-red-800 p-4 rounded mb-4">{error}</div>}

        {/* Mode Selector */}
        <div className="mb-6">
          <div className="flex gap-3 bg-gray-100 p-2 rounded-lg inline-flex">
            <button
              onClick={() => handleModeChange('dashboard')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                mode === 'dashboard'
                  ? 'bg-white text-blue-600 shadow'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              🏠 Dashboard
            </button>
            <button
              onClick={() => handleModeChange('hr')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                mode === 'hr'
                  ? 'bg-white text-blue-600 shadow'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              👥 HR
            </button>
            <button
              onClick={() => handleModeChange('duty-manager')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                mode === 'duty-manager'
                  ? 'bg-white text-blue-600 shadow'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              📋 On Duty Manager
            </button>
            <button
              onClick={() => handleModeChange('admin')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                mode === 'admin'
                  ? 'bg-white text-blue-600 shadow'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              ⚙️ Admin
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6 flex gap-2 border-b">
          {mode === 'hr' && (
            <>
              <button
                onClick={() => handleTabChange('applications')}
                className={`px-4 py-2 font-medium ${
                  activeTab === 'applications'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600'
                }`}
              >
                Applications {applications.length > 0 && `(${applications.length})`}
              </button>
              <button
                onClick={() => handleTabChange('waitlist')}
                className={`px-4 py-2 font-medium ${
                  activeTab === 'waitlist'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600'
                }`}
              >
                Waiting List {waitlistUsers.length > 0 && `(${waitlistUsers.length})`}
              </button>
              <button
                onClick={() => handleTabChange('assignments')}
                className={`px-4 py-2 font-medium ${
                  activeTab === 'assignments'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600'
                }`}
              >
                Assignments
              </button>
              <button
                onClick={() => handleTabChange('users')}
                className={`px-4 py-2 font-medium ${
                  activeTab === 'users'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600'
                }`}
              >
                Users
              </button>
              <button
                onClick={() => handleTabChange('withdrawals')}
                className={`px-4 py-2 font-medium ${
                  activeTab === 'withdrawals'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600'
                }`}
              >
                Withdrawals {withdrawalRequests.filter(w => w.status === 'pending').length > 0 && `(${withdrawalRequests.filter(w => w.status === 'pending').length})`}
              </button>
            </>
          )}

          {mode === 'duty-manager' && (
            <>
              <button
                onClick={() => handleTabChange('assignments')}
                className={`px-4 py-2 font-medium ${
                  activeTab === 'assignments'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600'
                }`}
              >
                Assignments
              </button>
              <button
                onClick={() => handleTabChange('deviations')}
                className={`px-4 py-2 font-medium ${
                  activeTab === 'deviations'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600'
                }`}
              >
                Deviations {deviations.filter(d => !d.absence_handled).length > 0 && `(${deviations.filter(d => !d.absence_handled).length})`}
              </button>
              <button
                onClick={() => handleTabChange('sick-notes')}
                className={`px-4 py-2 font-medium ${
                  activeTab === 'sick-notes'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600'
                }`}
              >
                Sick Notes {sickNotes.filter(s => s.status === 'pending').length > 0 && `(${sickNotes.filter(s => s.status === 'pending').length})`}
              </button>
            </>
          )}

          {mode === 'admin' && (
            <>
              <button
                onClick={() => handleTabChange('shifts')}
                className={`px-4 py-2 font-medium ${
                  activeTab === 'shifts'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600'
                }`}
              >
                Shifts {shifts.length > 0 && `(${shifts.length})`}
              </button>
              <button
                onClick={() => handleTabChange('constraints')}
                className={`px-4 py-2 font-medium ${
                  activeTab === 'constraints'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600'
                }`}
              >
                Constraints
              </button>
              <button
                onClick={() => handleTabChange('settings')}
                className={`px-4 py-2 font-medium ${
                  activeTab === 'settings'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600'
                }`}
              >
                Settings
              </button>
              <button
                onClick={() => handleTabChange('logs')}
                className={`px-4 py-2 font-medium ${
                  activeTab === 'logs'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600'
                }`}
              >
                Logs
              </button>
              <button
                onClick={() => handleTabChange('news')}
                className={`px-4 py-2 font-medium ${
                  activeTab === 'news'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600'
                }`}
              >
                News
              </button>
            </>
          )}
        </div>

        {activeTab === 'logs' && (
        <div className="grid grid-cols-1 gap-6">
          <section className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between border-b pb-3 mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Activity Logs</h2>
                <p className="text-xs text-gray-500 mt-1">Total storage: {formatBytes(authLogTotalStorageBytes)}</p>
              </div>
              <button
                onClick={() => refreshAuthLogs()}
                disabled={authLogsLoading}
                className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border border-gray-300 disabled:bg-gray-200 disabled:cursor-not-allowed"
              >
                {authLogsLoading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                <select
                  value={authLogTypeFilter}
                  onChange={(e) => setAuthLogTypeFilter(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm"
                >
                  <option value="">All types</option>
                  {authLogTypeOptions.map((eventType) => (
                    <option key={eventType} value={eventType}>{eventType}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                <input
                  type="text"
                  value={authLogEmailFilter}
                  onChange={(e) => setAuthLogEmailFilter(e.target.value)}
                  placeholder="Filter by email"
                  className="w-full border rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
                <input
                  type="date"
                  value={authLogFromFilter}
                  onChange={(e) => setAuthLogFromFilter(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
                <input
                  type="date"
                  value={authLogToFilter}
                  onChange={(e) => setAuthLogToFilter(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm"
                />
              </div>
              <div className="flex items-end gap-2">
                <button
                  onClick={() => refreshAuthLogs()}
                  className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                >
                  Apply
                </button>
                <button
                  onClick={() => {
                    const defaults = getDefaultLogDateRange();
                    setAuthLogTypeFilter('');
                    setAuthLogEmailFilter('');
                    setAuthLogFromFilter(defaults.from);
                    setAuthLogToFilter(defaults.to);
                    refreshAuthLogs({ eventType: '', email: '', from: defaults.from, to: defaults.to });
                  }}
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm border border-gray-300"
                >
                  Reset
                </button>
              </div>
            </div>

            <div className="border rounded-lg max-h-[65vh] overflow-auto bg-gray-50">
              {authLogs.length === 0 ? (
                <div className="p-4 text-sm text-gray-500">No log entries found for current filter.</div>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {authLogs.map((log) => (
                    <li key={log.id} className="p-3 text-sm text-gray-800">
                      <div className="font-mono text-xs text-gray-500">{new Date(log.created_at).toLocaleString('en-GB')}</div>
                      <div><span className="font-semibold">{log.event_type}</span>{log.email && <span className="ml-2 text-gray-600">— {log.email}</span>}</div>
                      {log.details && Object.keys(log.details).length > 0 && (
                        <pre className="mt-1 text-xs text-gray-600 whitespace-pre-wrap break-words">{JSON.stringify(log.details)}</pre>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>
        )}

        {activeTab === 'overview' && (
        <div className="space-y-6 mb-8">
          {/* Action-required counters */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Needs attention</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {/* Open applications */}
              {(() => {
                const count = applications.length;
                return (
                  <button
                    onClick={() => { setMode('hr'); localStorage.setItem('adminMode', 'hr'); handleTabChange('applications'); }}
                    className={`bg-white shadow rounded-lg p-4 text-left hover:shadow-md transition-shadow border-l-4 ${count > 0 ? 'border-orange-400' : 'border-gray-200'}`}
                  >
                    <p className="text-xs text-gray-500 mb-1">Open Applications</p>
                    <p className={`text-3xl font-bold ${count > 0 ? 'text-orange-600' : 'text-gray-400'}`}>{count}</p>
                    <p className="text-xs text-gray-400 mt-1">pending review</p>
                  </button>
                );
              })()}

              {/* Waiting list */}
              {(() => {
                const count = waitlistUsers.length;
                return (
                  <button
                    onClick={() => { setMode('hr'); localStorage.setItem('adminMode', 'hr'); handleTabChange('waitlist'); }}
                    className="bg-white shadow rounded-lg p-4 text-left hover:shadow-md transition-shadow border-l-4 border-blue-300"
                  >
                    <p className="text-xs text-gray-500 mb-1">Waiting List</p>
                    <p className="text-3xl font-bold text-blue-600">{count}</p>
                    <p className="text-xs text-gray-400 mt-1">on the list</p>
                  </button>
                );
              })()}

              {/* Withdrawal requests */}
              {(() => {
                const pending = withdrawalRequests.filter((w: any) => w.status === 'pending').length;
                return (
                  <button
                    onClick={() => { setMode('hr'); localStorage.setItem('adminMode', 'hr'); handleTabChange('withdrawals'); }}
                    className={`bg-white shadow rounded-lg p-4 text-left hover:shadow-md transition-shadow border-l-4 ${pending > 0 ? 'border-red-400' : 'border-gray-200'}`}
                  >
                    <p className="text-xs text-gray-500 mb-1">Withdrawal Requests</p>
                    <p className={`text-3xl font-bold ${pending > 0 ? 'text-red-600' : 'text-gray-400'}`}>{pending}</p>
                    <p className="text-xs text-gray-400 mt-1">pending</p>
                  </button>
                );
              })()}

              {/* Sick notes */}
              {(() => {
                const pending = sickNotes.filter((s: any) => s.status === 'pending').length;
                return (
                  <button
                    onClick={() => { setMode('duty-manager'); localStorage.setItem('adminMode', 'duty-manager'); handleTabChange('sick-notes'); }}
                    className={`bg-white shadow rounded-lg p-4 text-left hover:shadow-md transition-shadow border-l-4 ${pending > 0 ? 'border-yellow-400' : 'border-gray-200'}`}
                  >
                    <p className="text-xs text-gray-500 mb-1">Sick Notes</p>
                    <p className={`text-3xl font-bold ${pending > 0 ? 'text-yellow-600' : 'text-gray-400'}`}>{pending}</p>
                    <p className="text-xs text-gray-400 mt-1">pending</p>
                  </button>
                );
              })()}

              {/* Deviations */}
              {(() => {
                const unhandled = deviations.filter((d: any) => !d.absence_handled).length;
                return (
                  <button
                    onClick={() => { setMode('duty-manager'); localStorage.setItem('adminMode', 'duty-manager'); handleTabChange('deviations'); }}
                    className={`bg-white shadow rounded-lg p-4 text-left hover:shadow-md transition-shadow border-l-4 ${unhandled > 0 ? 'border-purple-400' : 'border-gray-200'}`}
                  >
                    <p className="text-xs text-gray-500 mb-1">Deviations</p>
                    <p className={`text-3xl font-bold ${unhandled > 0 ? 'text-purple-600' : 'text-gray-400'}`}>{unhandled}</p>
                    <p className="text-xs text-gray-400 mt-1">unhandled</p>
                  </button>
                );
              })()}

              {/* Confirmed volunteers (volunteer + supervisor) */}
              {(() => {
                const total = usersByRole
                  .filter((r: UsersByRole) => r.role === 'volunteer' || r.role === 'supervisor')
                  .reduce((sum: number, r: UsersByRole) => sum + parseNumber(r.count), 0);
                return (
                  <button
                    onClick={() => { setMode('hr'); localStorage.setItem('adminMode', 'hr'); handleTabChange('users'); }}
                    className="bg-white shadow rounded-lg p-4 text-left hover:shadow-md transition-shadow border-l-4 border-green-300"
                  >
                    <p className="text-xs text-gray-500 mb-1">Confirmed Volunteers</p>
                    <p className="text-3xl font-bold text-green-600">{total}</p>
                    <p className="text-xs text-gray-400 mt-1">volunteers + supervisors</p>
                  </button>
                );
              })()}
            </div>
          </div>

          {/* Confirmed users by role */}
          {usersByRole.filter((r: UsersByRole) => r.role !== 'guest').length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Confirmed users by role</h3>
              <div className="flex flex-wrap gap-3">
                {usersByRole.filter((r: UsersByRole) => r.role !== 'guest').map((r: UsersByRole) => (
                  <div key={r.role} className="bg-white shadow rounded-lg px-5 py-3 flex items-center gap-3">
                    <span className="text-2xl font-bold text-gray-800">{parseNumber(r.count)}</span>
                    <span className="text-sm text-gray-500 capitalize">{r.role}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Shift & booking summary */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Shift overview</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-white shadow rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Total Shifts</p>
                <p className="text-2xl font-bold text-gray-800">{parseNumber(shiftStats?.total_shifts)}</p>
                <p className="text-xs text-gray-400 mt-1">{parseNumber(shiftStats?.full_shifts)} full</p>
              </div>
              <div className="bg-white shadow rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Total Capacity</p>
                <p className="text-2xl font-bold text-gray-800">{parseNumber(shiftStats?.total_capacity)}</p>
                <p className="text-xs text-gray-400 mt-1">spots total</p>
              </div>
              <div className="bg-white shadow rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Booked</p>
                <p className="text-2xl font-bold text-gray-800">{parseNumber(shiftStats?.total_booked)}</p>
                {shiftStats?.total_capacity ? (
                  <p className="text-xs text-gray-400 mt-1">{Math.round((parseNumber(shiftStats.total_booked) / parseNumber(shiftStats.total_capacity)) * 100)}% filled</p>
                ) : (
                  <p className="text-xs text-gray-400 mt-1">—</p>
                )}
              </div>
              <div className="bg-white shadow rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Total Bookings</p>
                <p className="text-2xl font-bold text-gray-800">{parseNumber(bookingStats?.total_bookings)}</p>
                <p className="text-xs text-gray-400 mt-1">{parseNumber(bookingStats?.confirmed)} confirmed · {parseNumber(bookingStats?.cancelled)} cancelled</p>
              </div>
            </div>
          </div>
        </div>
        )}

        {activeTab === 'sick-notes' && (
        <div className="grid grid-cols-1 gap-6">
          <section className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Sick Requests</h2>
                <p className="text-gray-600 mt-1">Review volunteer sick submissions and take action.</p>
              </div>
              <div className="flex items-center gap-4">
                <p className="text-sm text-gray-500">Pending: {sickNotes.filter(s => s.status === 'pending').length}</p>
                <button
                  onClick={() => setShowCompletedSickNotes(!showCompletedSickNotes)}
                  className={`px-3 py-1 rounded text-sm font-medium ${
                    showCompletedSickNotes
                      ? 'bg-gray-600 text-white'
                      : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                  }`}
                >
                  {showCompletedSickNotes ? 'Hide' : 'Show'} Completed
                </button>
              </div>
            </div>

            {sickNotes.length === 0 ? (
              <p className="text-gray-500 text-center py-6">No sick requests yet.</p>
            ) : (
              <div className="space-y-4">
                {sickNotes.filter(note => showCompletedSickNotes || (note.status !== 'reschedule_accepted' && note.status !== 'declined')).map((note) => {
                  const originalStart = note.original_shift_start_time ? new Date(note.original_shift_start_time) : null;
                  const originalEnd = note.original_shift_end_time ? new Date(note.original_shift_end_time) : null;
                  const newStart = note.new_shift_start_time ? new Date(note.new_shift_start_time) : null;
                  const newEnd = note.new_shift_end_time ? new Date(note.new_shift_end_time) : null;
                  return (
                    <div key={note.id} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-3">
                            <h3 
                              className="font-semibold text-gray-900 cursor-pointer hover:text-blue-600 hover:underline"
                              onClick={() => openUserShiftsModal(note.user_id, note.name || note.user_name || 'Volunteer')}
                            >
                              {note.name || note.user_name || 'Volunteer'}
                            </h3>
                            <span className={`text-xs font-semibold px-2 py-1 rounded ${
                              note.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              note.status === 'reschedule_accepted' ? 'bg-green-100 text-green-800' :
                              note.status === 'doctor_required' ? 'bg-blue-100 text-blue-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {note.status.replace('_', ' ')}
                            </span>
                            {note.require_doctors_note && (
                              <span className="text-xs font-semibold px-2 py-1 rounded bg-red-100 text-red-800">Doctor note required (future)</span>
                            )}
                          </div>

                          <div className="text-sm text-gray-700 space-y-1">
                            <p><strong>Original shift:</strong> {note.original_shift_title || 'Shift'}{originalStart ? ` · ${originalStart.toLocaleString('en-GB', { hour12: false })}` : ''}{originalEnd ? ` – ${originalEnd.toLocaleTimeString('en-GB', { hour12: false })}` : ''}{note.original_shift_location ? ` (${note.original_shift_location})` : ''}</p>
                            {note.status === 'reschedule_accepted' && note.new_shift_title && (
                              <p><strong>Rescheduled to:</strong> {note.new_shift_title}{newStart ? ` · ${newStart.toLocaleString('en-GB', { hour12: false })}` : ''}{newEnd ? ` – ${newEnd.toLocaleTimeString('en-GB', { hour12: false })}` : ''}</p>
                            )}
                            <p>Plan: {note.plan === 'reschedule' ? 'Reschedule and stay' : 'Leave festival'}</p>
                            {note.reschedule_days && <p>Reschedule days: {note.reschedule_days}</p>}
                            <p>Phone: {note.phone || '—'}</p>
                            <p>Email: {note.email || '—'}</p>
                          </div>

                          {(note.wristband_photo || note.doctor_note_photo) && (
                            <div className="text-sm text-gray-700 space-y-2">
                              <p className="font-semibold">Attachments</p>
                              {note.wristband_photo && (
                                <div>
                                  <p className="text-xs text-gray-600 mb-1">Wristband photo</p>
                                  <button
                                    onClick={() => {
                                      setImagePreviewUrl(note.wristband_photo ?? null);
                                      setImagePreviewTitle('Wristband Photo');
                                    }}
                                    className="text-blue-600 hover:underline text-sm font-medium"
                                  >
                                    View image
                                  </button>
                                </div>
                              )}
                              {note.doctor_note_photo && (
                                <div>
                                  <p className="text-xs text-gray-600 mb-1">Doctor note</p>
                                  <button
                                    onClick={() => {
                                      setImagePreviewUrl(note.doctor_note_photo ?? null);
                                      setImagePreviewTitle('Doctor Note');
                                    }}
                                    className="text-blue-600 hover:underline text-sm font-medium"
                                  >
                                    View image
                                  </button>
                                </div>
                              )}
                            </div>
                          )}

                          <div className="space-y-2">
                            <label className="text-xs font-semibold text-gray-700">Admin notes</label>
                            <textarea
                              value={sickAdminNotes[note.id] ?? note.admin_notes ?? ''}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                setSickAdminNotes((prev) => ({ ...prev, [note.id]: newValue }));
                                autoSaveSickNote(note.id, newValue, note);
                              }}
                              className="w-full border rounded px-3 py-2 text-sm"
                              placeholder="Optional notes or instructions"
                            />
                          </div>
                        </div>

                        <div className="flex flex-col gap-2 w-48">
                          {note.plan === 'reschedule' ? (
                            <>
                              <button
                                onClick={() => startSickNoteReschedule(note)}
                                className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-semibold"
                              >
                                Reschedule shift
                              </button>
                              <button
                                onClick={() => handleUpdateSickNote(note.id, 'reschedule_accepted', note.require_doctors_note, sickAdminNotes[note.id])}
                                className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm font-semibold"
                              >
                                Accept (without rescheduling)
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleLeaveFestival(note.id, note.user_id, note.name || note.user_name || 'Volunteer')}
                                className="px-3 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 text-sm font-semibold"
                              >
                                Remove all future shifts
                              </button>
                              <button
                                onClick={() => handleUpdateSickNote(note.id, 'reschedule_accepted', note.require_doctors_note, sickAdminNotes[note.id])}
                                className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm font-semibold"
                              >
                                Accept (no action)
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleUpdateSickNote(note.id, 'declined', note.require_doctors_note, sickAdminNotes[note.id])}
                            className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm font-semibold"
                          >
                            Decline
                          </button>
                          <button
                            onClick={() => handleDeleteSickNote(note.id)}
                            className="px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm font-semibold"
                          >
                            Delete
                          </button>
                          <label className="flex items-center gap-2 text-sm text-gray-800 mt-2">
                            <input
                              type="checkbox"
                              checked={!!note.require_doctors_note}
                              onChange={(e) => handleUpdateSickNote(note.id, note.status, e.target.checked, sickAdminNotes[note.id])}
                            />
                            Require doctor note for future
                          </label>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
        )}

        {activeTab === 'withdrawals' && (
        <div>
          <section className="bg-white shadow rounded p-4">
            <h2 className="text-lg font-semibold mb-4">Volunteer Withdrawal Requests</h2>
            {withdrawalRequests.length === 0 ? (
              <p className="text-gray-600">No withdrawal requests</p>
            ) : (
              <div className="space-y-4">
                {withdrawalRequests.map((request) => (
                  <div key={request.id} className={`border rounded p-4 ${
                    request.status === 'pending' ? 'bg-yellow-50' :
                    request.status === 'approved' ? 'bg-green-50' :
                    'bg-gray-50'
                  }`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900">{request.user_name}</h3>
                          <span className={`text-xs font-semibold px-2 py-1 rounded ${
                            request.status === 'pending' ? 'bg-yellow-200 text-yellow-800' :
                            request.status === 'approved' ? 'bg-green-200 text-green-800' :
                            'bg-red-200 text-red-800'
                          }`}>
                            {request.status.toUpperCase()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{request.user_email}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Submitted: {new Date(request.created_at).toLocaleString('en-GB')}
                        </p>
                        {request.future_shifts_count !== undefined && (
                          <p className="text-xs text-gray-600 mt-1">
                            Future shifts: {request.future_shifts_count}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">Reason:</p>
                      <p className="text-sm text-gray-800 bg-white rounded p-2 border">{request.reason}</p>
                    </div>

                    {request.admin_notes && (
                      <div className="mb-3">
                        <p className="text-sm font-medium text-gray-700 mb-1">Admin Notes:</p>
                        <p className="text-sm text-gray-800 bg-white rounded p-2 border">{request.admin_notes}</p>
                      </div>
                    )}

                    {request.handled_at && (
                      <p className="text-xs text-gray-500 mb-2">
                        Handled by {request.handled_by_name || 'unknown'} on {new Date(request.handled_at).toLocaleString('en-GB')}
                      </p>
                    )}

                    {request.status === 'pending' && (
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => openApproveWithdrawalModal(request)}
                          className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm font-semibold"
                        >
                          Approve
                        </button>
                        <button
                          onClick={async () => {
                            const adminNotes = prompt('Reason for rejection (optional):');
                            try {
                              await adminWithdrawalService.update(request.id, {
                                status: 'rejected',
                                admin_notes: adminNotes || undefined,
                              });
                              await loadAll();
                            } catch (err: any) {
                              alert(err.response?.data?.error || 'Failed to reject withdrawal');
                            }
                          }}
                          className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm font-semibold"
                        >
                          Reject
                        </button>
                      </div>
                    )}
                    {request.status !== 'pending' && (
                      <button
                        onClick={async () => {
                          if (!confirm('Delete this withdrawal request?')) return;
                          try {
                            await adminWithdrawalService.delete(request.id);
                            await loadAll();
                          } catch (err: any) {
                            alert(err.response?.data?.error || 'Failed to delete withdrawal');
                          }
                        }}
                        className="px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm font-semibold mt-2"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
        )}

        {activeTab === 'applications' && (
        <section className="bg-white shadow rounded p-4 mb-8">
          <h2 className="text-lg font-semibold mb-4">Pending Volunteer Applications</h2>
          {applications.length === 0 ? (
            <p className="text-gray-600">No pending applications</p>
          ) : (
            <div className="space-y-4">
              {applications.map((app) => (
                <div key={app.id} className="border rounded p-4 bg-gray-50">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-600">Name</p>
                      <p className="font-medium">{app.full_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-medium">{app.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Phone</p>
                      <p className="font-medium">{app.phone}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Previous Volunteer</p>
                      <p className="font-medium">{app.previous_volunteer || 'No'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Date of Birth</p>
                      <p className="font-medium">{app.date_of_birth || '—'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">DTU Member</p>
                      <p className="font-medium">
                        {app.dtu_member_status === 'yes_student_employee'
                          ? 'Yes (Student, Employee, ...)'
                          : app.dtu_member_status === 'yes_alumni'
                          ? 'Yes (Alumni)'
                          : app.dtu_member === true
                          ? 'Yes'
                          : 'No'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Has People-Vol Profile</p>
                      <p className="font-medium">
                        {app.has_peoplevol_profile === true ? (
                          <span className="text-green-600">✓ Yes - Existing Profile</span>
                        ) : app.has_peoplevol_profile === false ? (
                          app.confirmed_peoplevol_registration ? (
                            <span className="text-blue-600">✓ New - Registered</span>
                          ) : (
                            <span className="text-gray-500">New - Not confirmed</span>
                          )
                        ) : (
                          <span className="flex items-center gap-2 flex-wrap mt-1">
                            <span className="text-gray-400">Not specified</span>
                            <button
                              onClick={async () => {
                                if (!confirm(`Send incomplete application reminder to ${app.email || app.name}?`)) return;
                                try {
                                  const res = await volunteerApplicationService.sendIncompleteReminder(app.id);
                                  setApplications(prev => prev.map(a => a.id === app.id ? { ...a, incomplete_reminder_sent_at: res.data.sent_at } : a));
                                } catch (err: any) {
                                  alert(err.response?.data?.error || 'Failed to send reminder');
                                }
                              }}
                              className="px-2 py-0.5 text-xs bg-amber-100 text-amber-800 border border-amber-300 rounded hover:bg-amber-200 whitespace-nowrap"
                            >
                              Send reminder
                            </button>
                            {app.incomplete_reminder_sent_at && (
                              <span className="text-xs text-gray-500 whitespace-nowrap">
                                Last sent: {new Date(app.incomplete_reminder_sent_at).toLocaleString()}
                              </span>
                            )}
                          </span>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Applied</p>
                      <p className="font-medium">{new Date(app.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {approvingApplicationId === app.id ? (
                      <>
                        <div className="flex items-center gap-2">
                          <label className="text-sm font-medium">Assign Role:</label>
                          <select
                            value={approvingRole}
                            onChange={(e) => setApprovingRole(e.target.value)}
                            className="border rounded px-3 py-2"
                          >
                            <option value="volunteer">Volunteer</option>
                            <option value="supervisor">Supervisor</option>
                            <option value="admin">Admin</option>
                          </select>
                        </div>
                        <button
                          onClick={() => handleApproveApplication(app.id)}
                          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => {
                            setApprovingApplicationId(null);
                            setApprovingRole('volunteer');
                          }}
                          className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => setApprovingApplicationId(app.id)}
                          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                        >
                          Approve
                        </button>
                        <button
                          onClick={async () => {
                            if (confirm(`Move ${app.full_name} to the waiting list?`)) {
                              await adminService.addToWaitlist(app.user_id);
                              await loadAll();
                            }
                          }}
                          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          → Waiting List
                        </button>
                        <button
                          onClick={() => handleRejectApplication(app.id)}
                          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                        >
                          Reject
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
        )}

        {activeTab === 'waitlist' && (
        <section className="bg-white shadow rounded p-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Waiting List ({waitlistUsers.length})</h2>
            <div className="flex gap-2 items-center">
              <button
                onClick={() => {
                  const eligible = waitlistUsers.filter((u) => {
                    const hasOffer = u.waitlist_offer_sent_at && u.waitlist_offer_expires_at && new Date(u.waitlist_offer_expires_at) > new Date();
                    const offerExpired = u.waitlist_offer_sent_at && u.waitlist_offer_expires_at && new Date(u.waitlist_offer_expires_at) <= new Date();
                    const offerAccepted = u.waitlist_offer_response === 'accepted';
                    return !hasOffer && !offerExpired && !offerAccepted;
                  });
                  const raw = window.prompt(`How many people to select randomly? (${eligible.length} eligible)`);
                  if (raw === null) return;
                  const n = parseInt(raw, 10);
                  if (isNaN(n) || n <= 0) return;
                  const count = Math.min(n, eligible.length);
                  const shuffled = [...eligible].sort(() => Math.random() - 0.5);
                  setSelectedWaitlistUserIds(new Set(shuffled.slice(0, count).map((u) => u.id)));
                }}
                className="px-4 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 font-medium text-sm"
              >
                Select Random
              </button>
              {selectedWaitlistUserIds.size >= 2 && (
                <>
                  <button
                    onClick={() => setShowBatchInviteConfirm(true)}
                    disabled={sendingBatchOffers}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 font-semibold"
                  >
                    {sendingBatchOffers ? 'Sending...' : `Batch Invite (${selectedWaitlistUserIds.size})`}
                  </button>
                  <button
                    onClick={() => setSelectedWaitlistUserIds(new Set())}
                    className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500"
                  >
                    Clear Selection
                  </button>
                </>
              )}
            </div>
          </div>
          {waitlistUsers.length === 0 ? (
            <p className="text-gray-600">No users on the waiting list.</p>
          ) : (
            <div className="space-y-4">
              {waitlistUsers.map((user) => {
                const hasOffer = user.waitlist_offer_sent_at && user.waitlist_offer_expires_at && new Date(user.waitlist_offer_expires_at) > new Date();
                const offerExpired = user.waitlist_offer_sent_at && user.waitlist_offer_expires_at && new Date(user.waitlist_offer_expires_at) <= new Date();
                const offerAccepted = user.waitlist_offer_response === 'accepted';
                const isSelected = selectedWaitlistUserIds.has(user.id);
                
                // Get batch partners (other users in the same batch)
                const batchPartners = user.batch_offer_id 
                  ? waitlistUsers.filter(u => u.batch_offer_id === user.batch_offer_id && u.id !== user.id)
                  : [];
                
                return (
                <div key={user.id} className={`border rounded p-4 transition-colors ${
                  offerAccepted ? 'border-purple-400 bg-purple-50' :
                  hasOffer ? 'border-green-400 bg-green-50' : 'hover:border-blue-400'
                } ${isSelected ? 'ring-2 ring-blue-400 bg-blue-50' : ''}`}>
                  <div className="grid grid-cols-1 md:grid-cols-7 gap-4 items-center">
                    <div>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          const newSet = new Set(selectedWaitlistUserIds);
                          if (e.target.checked) {
                            newSet.add(user.id);
                          } else {
                            newSet.delete(user.id);
                          }
                          setSelectedWaitlistUserIds(newSet);
                        }}
                        disabled={hasOffer || offerExpired || offerAccepted}
                        className="w-5 h-5 cursor-pointer"
                        title={hasOffer || offerExpired || offerAccepted ? 'Cannot select users with active offers' : 'Select for batch invite'}
                      />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Position</p>
                      <p className="font-bold text-2xl text-blue-600">#{user.display_position}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Name</p>
                      <p className="font-medium">{user.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">DTU?</p>
                      <p className="font-medium">
                        {user.dtu_member_status && user.dtu_member_status !== 'no' ? 'Yes' : 'No'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Previous volunteer?</p>
                      <p className="font-medium">{user.previous_volunteer || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Date Applied</p>
                      <p className="font-medium">{user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Profile</p>
                      <Link to={`/admin/users/${user.id}`} className="font-medium text-blue-600 hover:text-blue-800 underline">
                        Profile
                      </Link>
                    </div>
                  </div>
                  {offerAccepted && (
                    <div className="mt-3 p-3 bg-purple-100 border border-purple-400 rounded">
                      <div className="flex justify-between items-center gap-3">
                        <div>
                          <p className="text-sm font-semibold text-purple-900">✅ Offer Accepted - Awaiting Confirmation</p>
                          <p className="text-sm text-purple-800 mt-1">
                            User has accepted the offer and is ready to become a volunteer
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={async () => {
                              if (confirm(`Do not confirm ${user.name} now and return them to normal waiting list status?`)) {
                                try {
                                  await adminService.doNotConfirmAcceptance(user.id);
                                  alert(`${user.name} returned to waiting list and can be offered again`);
                                  await loadAll();
                                } catch (err: any) {
                                  alert(err.response?.data?.error || 'Failed to return user to waiting list');
                                }
                              }
                            }}
                            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 font-semibold whitespace-nowrap"
                          >
                            Do Not Confirm
                          </button>
                          <button
                            onClick={async () => {
                              if (confirm(`Confirm ${user.name} as volunteer?`)) {
                                try {
                                  await adminService.confirmAcceptance(user.id);
                                  alert(`${user.name} is now confirmed as a volunteer`);
                                  await loadAll();
                                } catch (err: any) {
                                  alert(err.response?.data?.error || 'Failed to confirm acceptance');
                                }
                              }
                            }}
                            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 font-semibold whitespace-nowrap"
                          >
                            Confirm as Volunteer
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {hasOffer && !offerAccepted && (
                    <div className="mt-3 p-3 bg-green-100 border border-green-300 rounded">
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-green-900">✉️ Offer Sent</p>
                          <div className="text-sm text-green-800 mt-2 space-y-1">
                            <p>
                              Type: <span className="font-medium">{user.offer_type === 'batch' ? `Batch (${user.batch_size} users)` : 'Individual'}</span>
                            </p>
                            <p>
                              Expires: {user.waitlist_offer_expires_at ? new Date(user.waitlist_offer_expires_at).toLocaleString() : 'N/A'}
                            </p>
                            {batchPartners.length > 0 && (
                              <div className="mt-2 p-2 bg-green-50 rounded border border-green-200">
                                <p className="text-xs font-semibold text-green-700 mb-1">Batch partners:</p>
                                <ul className="text-xs text-green-700 space-y-1">
                                  {batchPartners.map(partner => (
                                    <li key={partner.id}>• {partner.name} (Position #{partner.display_position})</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {user.batch_offer_id && (
                              <p className="text-xs text-green-700">
                                Batch ID: {user.batch_offer_id}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={async () => {
                              if (confirm(`${user.offer_type === 'batch' ? `Cancel offer for all ${user.batch_size} users in this batch?` : `Cancel offer for ${user.name}?`}`)) {
                                try {
                                  await adminService.cancelOffer(user.id);
                                  alert('Offer cancelled successfully');
                                  await loadAll();
                                } catch (err: any) {
                                  alert(err.response?.data?.error || 'Failed to cancel offer');
                                }
                              }
                            }}
                            className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 whitespace-nowrap font-semibold"
                            title={user.offer_type === 'batch' ? 'Cancel offer for entire batch' : 'Cancel offer'}
                          >
                            ✕ Cancel Offer
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                await adminService.testReduceOfferDeadline(user.id);
                                alert('Deadline reduced by 1 hour for testing');
                                await loadAll();
                              } catch (err: any) {
                                alert(err.response?.data?.error || 'Failed to reduce deadline');
                              }
                            }}
                            className="px-2 py-1 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600 whitespace-nowrap"
                            title="Testing: Reduce deadline by 1 hour"
                          >
                            -1h (test)
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {offerExpired && (
                    <div className="mt-3 p-3 bg-yellow-100 border border-yellow-300 rounded">
                      <p className="text-sm font-semibold text-yellow-900">⏰ Offer Expired</p>
                      <p className="text-sm text-yellow-800 mt-1">
                        Expired: {user.waitlist_offer_expires_at ? new Date(user.waitlist_offer_expires_at).toLocaleString() : 'N/A'}
                      </p>
                    </div>
                  )}
                  {openWaitlistMoveUserId === user.id && (
                    <div className="mt-3 p-3 bg-indigo-50 border border-indigo-200 rounded">
                      <div className="flex items-center gap-2 flex-wrap">
                        <label className="text-sm text-gray-700">Move to</label>
                        <input
                          type="number"
                          min={1}
                          value={waitlistMoveTargets[user.id] ?? String(user.display_position || user.waiting_list_position || '')}
                          onChange={(e) =>
                            setWaitlistMoveTargets((prev) => ({
                              ...prev,
                              [user.id]: e.target.value,
                            }))
                          }
                          className="w-24 border rounded px-2 py-2 text-sm"
                        />
                        <button
                          onClick={() => handleMoveWaitlistPosition(user)}
                          disabled={movingWaitlistUserId === user.id}
                          className="px-3 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-gray-400 text-sm"
                        >
                          {movingWaitlistUserId === user.id ? 'Moving...' : 'Confirm Move'}
                        </button>
                        <button
                          onClick={() => setOpenWaitlistMoveUserId(null)}
                          className="px-3 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => {
                        setOpenWaitlistMoveUserId((current) => {
                          const next = current === user.id ? null : user.id;
                          if (next === user.id && waitlistMoveTargets[user.id] === undefined) {
                            setWaitlistMoveTargets((prev) => ({
                              ...prev,
                              [user.id]: String(user.display_position || user.waiting_list_position || ''),
                            }));
                          }
                          return next;
                        });
                      }}
                      className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                    >
                      Move
                    </button>
                    <button
                      onClick={async () => {
                        if (confirm(`Send offer to ${user.name}? They will have ${globalSettings.waitlist_offer_hours} hours to respond.`)) {
                          try {
                            await adminService.sendWaitlistOffer(user.id);
                            alert(`Offer sent to ${user.name}`);
                            await loadAll();
                          } catch (err: any) {
                            alert(err.response?.data?.error || 'Failed to send offer');
                          }
                        }
                      }}
                      disabled={offerAccepted}
                      className={`px-4 py-2 rounded ${offerAccepted ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
                    >
                      Send Offer
                    </button>
                    <button
                      onClick={async () => {
                        if (confirm(`Directly approve ${user.name} as volunteer (bypasses offer flow)?`)) {
                          await adminService.approveFromWaitlist(user.id);
                          await loadAll();
                        }
                      }}
                      disabled={offerAccepted}
                      className={`px-4 py-2 rounded ${offerAccepted ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'} text-white`}
                    >
                      Direct Approve
                    </button>
                    <button
                      onClick={async () => {
                        if (confirm(`Remove ${user.name} from the waiting list?`)) {
                          await adminService.removeFromWaitlist(user.id);
                          await loadAll();
                        }
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              );
              })}
            </div>
          )}
          <div className="mt-6">
            <button
              onClick={() => setShowWaitlistImportForm(!showWaitlistImportForm)}
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 font-medium text-sm"
            >
              {showWaitlistImportForm ? 'Hide Import' : 'Import from CSV'}
            </button>
            {showWaitlistImportForm && (
              <div className="mt-4 border rounded-lg p-4 bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-800 mb-2">Import waitlist users from CSV</h3>
                <p className="text-xs text-gray-600 mb-3">Expected headers: Name;DOB;Role;People-Vol Registration;Phone (WhatsApp);Email;Days since Invite;Referred by;Are you from DTU?</p>
                <div className="flex flex-col gap-3">
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleWaitlistImportFile}
                    className="text-sm"
                  />
                  <textarea
                    rows={6}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="Paste CSV content here"
                    value={waitlistImportText}
                    onChange={(e) => setWaitlistImportText(e.target.value)}
                  />
                  {waitlistImportError && <p className="text-sm text-red-600">{waitlistImportError}</p>}
                  {waitlistImportResult && (
                    <div className="text-sm text-gray-700">
                      Imported: {waitlistImportResult.created} created, {waitlistImportResult.updated} updated, {waitlistImportResult.skipped} skipped
                      {waitlistImportResult.errors?.length > 0 && (
                        <ul className="mt-2 list-disc list-inside text-xs text-red-600">
                          {waitlistImportResult.errors.slice(0, 10).map((err, idx) => (
                            <li key={idx}>{err}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={handleImportWaitlist}
                      disabled={waitlistImportLoading}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                    >
                      {waitlistImportLoading ? 'Importing...' : 'Import to Waitlist'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setWaitlistImportText('');
                        setWaitlistImportError('');
                        setWaitlistImportResult(null);
                      }}
                      className="px-3 py-2 text-sm bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
        )}

        {activeTab === 'shifts' && (
        <div className="grid grid-cols-1 gap-6">
          <section className="bg-white shadow rounded p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">{editingShiftId ? 'Edit Shift' : 'Create New Shift'}</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleExportShiftPlan}
                  disabled={shiftPlanExportLoading}
                  className="bg-emerald-600 text-white px-4 py-2 rounded hover:bg-emerald-700 font-medium text-sm disabled:bg-gray-400"
                >
                  {shiftPlanExportLoading ? 'Exporting...' : 'Export Shifts'}
                </button>
                <label className={`bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 font-medium text-sm cursor-pointer ${shiftPlanImportLoading ? 'opacity-60 cursor-not-allowed' : ''}`}>
                  {shiftPlanImportLoading ? 'Importing...' : 'Import Shifts'}
                  <input
                    type="file"
                    accept="application/json,.json"
                    onChange={handleImportShiftPlanFile}
                    disabled={shiftPlanImportLoading}
                    className="hidden"
                  />
                </label>
                <button
                  onClick={() => setShowBulkCreate(!showBulkCreate)}
                  className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 font-medium text-sm"
                >
                  {showBulkCreate ? 'Single Shift' : 'Bulk Create'}
                </button>
              </div>
            </div>

            {showBulkCreate ? (
              <form onSubmit={handleBulkCreateShifts} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 pb-8 border-b">
                <div className="col-span-2 bg-purple-50 border border-purple-200 rounded-lg p-4 mb-2">
                  <h3 className="font-semibold text-purple-900 mb-2">Bulk Create Shifts</h3>
                  <p className="text-sm text-purple-700">Create multiple shifts across a date range with the same time and details. If end time is earlier than start time, shifts will automatically cross midnight.</p>
                </div>
                <input
                  type="text"
                  placeholder="Title (optional)"
                  value={bulkShiftForm.title}
                  onChange={(e) => setBulkShiftForm((p) => ({ ...p, title: e.target.value }))}
                  className="border rounded px-3 py-2 col-span-2"
                />
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    required
                    value={bulkShiftForm.start_date}
                    onChange={(e) => setBulkShiftForm((p) => ({ ...p, start_date: e.target.value }))}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    required
                    value={bulkShiftForm.end_date}
                    onChange={(e) => setBulkShiftForm((p) => ({ ...p, end_date: e.target.value }))}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Daily Start Time</label>
                  <input
                    type="time"
                    required
                    value={bulkShiftForm.start_time}
                    onChange={(e) => setBulkShiftForm((p) => ({ ...p, start_time: e.target.value }))}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Daily End Time</label>
                  <input
                    type="time"
                    required
                    value={bulkShiftForm.end_time}
                    onChange={(e) => setBulkShiftForm((p) => ({ ...p, end_time: e.target.value }))}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <input
                  type="text"
                  placeholder="Meeting Point"
                  value={bulkShiftForm.location}
                  onChange={(e) => setBulkShiftForm((p) => ({ ...p, location: e.target.value }))}
                  className="border rounded px-3 py-2 col-span-1"
                />
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Roles</label>
                  <div className="space-y-2">
                    {bulkShiftForm.role_requirements.map((req, idx) => {
                      const otherSelectedRoles = bulkShiftForm.role_requirements
                        .filter((_, i) => i !== idx)
                        .map(r => r.role_name)
                        .filter(r => r !== '');
                      const availableRoles = ['volunteer', 'supervisor', 'admin'].filter(role => !otherSelectedRoles.includes(role));
                      return (
                      <div key={idx} className="flex gap-2">
                        <select
                          value={req.role_name}
                          onChange={(e) => {
                            const updated = [...bulkShiftForm.role_requirements];
                            updated[idx].role_name = e.target.value;
                            setBulkShiftForm((p) => ({ ...p, role_requirements: updated }));
                          }}
                          className="flex-1 border rounded px-3 py-2"
                        >
                          {availableRoles.map(role => (
                            <option key={role} value={role}>{role.charAt(0).toUpperCase() + role.slice(1)}</option>
                          ))}
                        </select>
                        <input
                          type="number"
                          min={1}
                          placeholder="Max count"
                          value={req.max_count}
                          onChange={(e) => {
                            const updated = [...bulkShiftForm.role_requirements];
                            updated[idx].max_count = Number(e.target.value);
                            setBulkShiftForm((p) => ({ ...p, role_requirements: updated }));
                          }}
                          className="w-24 border rounded px-3 py-2"
                        />
                        {bulkShiftForm.role_requirements.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const updated = bulkShiftForm.role_requirements.filter((_, i) => i !== idx);
                            setBulkShiftForm((p) => ({ ...p, role_requirements: updated }));
                          }}
                          className="px-3 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200"
                        >
                          Remove
                        </button>
                        )}
                      </div>
                    );
                    })}
                    <button
                      type="button"
                      onClick={() => {
                        const selectedRoles = bulkShiftForm.role_requirements.map(r => r.role_name).filter(r => r !== '');
                        const firstAvailable = ['volunteer', 'supervisor', 'admin'].find(role => !selectedRoles.includes(role)) || '';
                        setBulkShiftForm((p) => ({ ...p, role_requirements: [...p.role_requirements, { role_name: firstAvailable, max_count: 1 }] }));
                      }}
                      className="px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm"
                    >
                      + Add Role Requirement
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {bulkShiftForm.role_requirements.length > 0 
                      ? `Total spots per shift: ${bulkShiftForm.role_requirements.reduce((sum, r) => sum + (r.max_count || 0), 0)}` 
                      : 'Leave empty to use default (10 spots for \'volunteer\' role)'}
                  </p>
                </div>
                <div className="col-span-1">
                  <div className="flex flex-wrap gap-2">
                    {tags.map((t) => (
                      <label key={t.id} className={`flex items-center gap-2 cursor-pointer px-3 py-1 rounded text-sm font-medium ${getTagColor(t.name)} opacity-100 hover:opacity-80 transition-opacity`}>
                        <input
                          type="checkbox"
                          checked={bulkShiftForm.tags.includes(t.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setBulkShiftForm((p) => ({ ...p, tags: [...p.tags, t.id] }));
                            } else {
                              setBulkShiftForm((p) => ({ ...p, tags: p.tags.filter(id => id !== t.id) }));
                            }
                          }}
                          className="w-4 h-4 rounded"
                        />
                        <span>{t.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="col-span-2">
                  <button
                    type="submit"
                    className="w-full bg-purple-600 text-white py-2 rounded hover:bg-purple-700 font-medium"
                  >
                    Create Bulk Shifts
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleCreateOrUpdateShift} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 pb-8 border-b">
                <input
                  type="text"
                  placeholder="Title (optional)"
                  value={shiftForm.title}
                  onChange={(e) => setShiftForm((p) => ({ ...p, title: e.target.value }))}
                  className="border rounded px-3 py-2 col-span-1"
                />
                <input
                  type="text"
                  placeholder="Meeting Point"
                  value={shiftForm.location}
                  onChange={(e) => setShiftForm((p) => ({ ...p, location: e.target.value }))}
                  className="border rounded px-3 py-2 col-span-1"
                />
                <input
                  type="datetime-local"
                  required
                  value={shiftForm.start_time}
                  onChange={(e) => setShiftForm((p) => ({ ...p, start_time: e.target.value }))}
                  className="border rounded px-3 py-2 col-span-1"
                />
                <input
                  type="datetime-local"
                  required
                  value={shiftForm.end_time}
                  onChange={(e) => setShiftForm((p) => ({ ...p, end_time: e.target.value }))}
                  className="border rounded px-3 py-2 col-span-1"
                />
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Roles</label>
                  <div className="space-y-2">
                    {shiftForm.role_requirements.map((req, idx) => {
                      const otherSelectedRoles = shiftForm.role_requirements
                        .filter((_, i) => i !== idx)
                        .map(r => r.role_name)
                        .filter(r => r !== '');
                      const availableRoles = ['volunteer', 'supervisor', 'admin'].filter(role => !otherSelectedRoles.includes(role));
                      return (
                      <div key={idx} className="flex gap-2">
                        <select
                          value={req.role_name}
                          onChange={(e) => {
                            const updated = [...shiftForm.role_requirements];
                            updated[idx].role_name = e.target.value;
                            setShiftForm((p) => ({ ...p, role_requirements: updated }));
                          }}
                          className="flex-1 border rounded px-3 py-2"
                        >
                          {availableRoles.map(role => (
                            <option key={role} value={role}>{role.charAt(0).toUpperCase() + role.slice(1)}</option>
                          ))}
                        </select>
                        <input
                          type="number"
                          min={1}
                          placeholder="Max count"
                          value={req.max_count}
                          onChange={(e) => {
                            const updated = [...shiftForm.role_requirements];
                            updated[idx].max_count = Number(e.target.value);
                            setShiftForm((p) => ({ ...p, role_requirements: updated }));
                          }}
                          className="w-24 border rounded px-3 py-2"
                        />
                        {shiftForm.role_requirements.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const updated = shiftForm.role_requirements.filter((_, i) => i !== idx);
                            setShiftForm((p) => ({ ...p, role_requirements: updated }));
                          }}
                          className="px-3 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200"
                        >
                          Remove
                        </button>
                        )}
                      </div>
                    );
                    })}
                    <button
                      type="button"
                      onClick={() => {
                        const selectedRoles = shiftForm.role_requirements.map(r => r.role_name).filter(r => r !== '');
                        const firstAvailable = ['volunteer', 'supervisor', 'admin'].find(role => !selectedRoles.includes(role)) || '';
                        setShiftForm((p) => ({ ...p, role_requirements: [...p.role_requirements, { role_name: firstAvailable, max_count: 1 }] }));
                      }}
                      className="px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm"
                    >
                      + Add Role Requirement
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {shiftForm.role_requirements.length > 0 
                      ? `Total spots: ${shiftForm.role_requirements.reduce((sum, r) => sum + (r.max_count || 0), 0)}` 
                      : 'Leave empty to use default (10 spots for \'volunteer\' role)'}
                  </p>
                </div>
                <div className="col-span-1">
                  <div className="flex flex-wrap gap-2">
                    {tags.map((t) => (
                      <label key={t.id} className={`flex items-center gap-2 cursor-pointer px-3 py-1 rounded text-sm font-medium ${getTagColor(t.name)} opacity-100 hover:opacity-80 transition-opacity`}>
                        <input
                          type="checkbox"
                          checked={shiftForm.tags.includes(t.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setShiftForm((p) => ({ ...p, tags: [...p.tags, t.id] }));
                            } else {
                              setShiftForm((p) => ({ ...p, tags: p.tags.filter(id => id !== t.id) }));
                            }
                          }}
                          className="w-4 h-4 rounded"
                        />
                        <span>{t.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="col-span-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!shiftForm.manual_full}
                      onChange={(e) => setShiftForm((p: any) => ({ ...p, manual_full: e.target.checked }))}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-sm">Mark as full (prevent new bookings)</span>
                  </label>
                </div>
                <div className="col-span-2 flex gap-2">
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
                  >
                    {editingShiftId ? 'Update Shift' : 'Create Shift'}
                  </button>
                  {editingShiftId && (
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="flex-1 bg-gray-400 text-white py-2 rounded hover:bg-gray-500"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            )}

            <h2 className="text-lg font-semibold mb-4">All Shifts</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-600 border-b">
                    <th className="py-2 pr-4">Title</th>
                    <th className="py-2 pr-4">Start Time</th>
                    <th className="py-2 pr-4">End Time</th>
                    {globalSettings.show_shift_location && <th className="py-2 pr-4">Meeting Point</th>}
                    <th className="py-2 pr-4">Capacity</th>
                    <th className="py-2 pr-4">Tags</th>
                    <th className="py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {shifts.map((shift) => {
                    const startStr = shift.start_time.substring(0, 16).replace('T', ' ');
                    const endStr = shift.end_time.substring(0, 16).replace('T', ' ');
                    const startDate = startStr.split(' ')[0];
                    const endDate = endStr.split(' ')[0];
                    const nextDaySuffix = startDate !== endDate ? ' (next day)' : '';

                    return (
                    <tr key={shift.id} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-medium">{shift.title}</td>
                      <td className="py-2 pr-4 text-sm">{startStr}</td>
                      <td className="py-2 pr-4 text-sm">{endStr}{nextDaySuffix}</td>
                      {globalSettings.show_shift_location && <td className="py-2 pr-4">{shift.location || '—'}</td>}
                      <td className="py-2 pr-4">
                        <div className="space-y-1">
                          {shift.role_requirements && shift.role_requirements.map((req: any, idx: number) => (
                            <div key={idx} className="text-xs">
                              <span className="font-medium capitalize">{req.role_name}s:</span>{' '}
                              <span className={`font-semibold ${
                                req.current_count >= req.max_count ? 'text-red-600' :
                                req.current_count / req.max_count >= 0.7 ? 'text-yellow-600' :
                                'text-green-600'
                              }`}>{req.current_count || 0}/{req.max_count}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="py-2 pr-4">
                        <div className="flex gap-1 flex-wrap">
                          {shift.manual_full && (
                            <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-700 border border-red-200">
                              Manually full
                            </span>
                          )}
                          {shift.tags && shift.tags.map((tag: any) => (
                            <span key={tag.id} className={`px-2 py-1 rounded text-xs font-medium ${getTagColor(tag.name)}`}>
                              {tag.name}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-2 flex gap-2">
                        <button
                          onClick={() => handleEditShift(shift)}
                          className="text-blue-600 hover:underline"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteShift(shift.id)}
                          className="text-red-600 hover:underline"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
              {shifts.length === 0 && <p className="text-gray-600 py-4">No shifts yet</p>}
            </div>
          </section>

          <section className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-bold mb-6 text-gray-900">Shift Tags</h2>
            <div className="space-y-4 max-w-2xl">
              <div className="space-y-3 mb-6">
                {tags.map((t) => (
                  <div key={t.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{t.name}</p>
                      {t.description && <p className="text-sm text-gray-600">{t.description}</p>}
                    </div>
                    <button 
                      onClick={() => handleDeleteTag(t.id)}
                      className="text-red-600 hover:text-red-800 hover:bg-red-50 px-3 py-1 rounded text-sm font-medium"
                    >
                      Delete
                    </button>
                  </div>
                ))}
                {tags.length === 0 && <p className="text-gray-500 text-center py-4">No tags yet. Create one below.</p>}
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-900 mb-3">Create New Tag</h3>
                <input
                  className="w-full border rounded-lg px-3 py-2 mb-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Tag name (e.g., 'morning', 'night', 'music-day')"
                  value={newTag.name}
                  onChange={(e) => setNewTag((p) => ({ ...p, name: e.target.value }))}
                />
                <input
                  className="w-full border rounded-lg px-3 py-2 mb-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Description (optional)"
                  value={newTag.description}
                  onChange={(e) => setNewTag((p) => ({ ...p, description: e.target.value }))}
                />
                <button
                  onClick={handleCreateTag}
                  className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 font-medium"
                >
                  Add Tag
                </button>
              </div>
            </div>
          </section>
        </div>
        )}

        {activeTab === 'assignments' && (
        <div className="grid grid-cols-1 gap-6">
          <section className="bg-white shadow rounded-lg p-6">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Shift Assignments</h2>
                <p className="text-gray-600 mt-2">Manage volunteer assignments for each shift. Add volunteers via per-shift buttons, reassign with a shift picker, or unassign.</p>
              </div>
              <button
                onClick={openSwapModal}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm font-medium"
              >
                Swap Shifts
              </button>
            </div>
            
            {shifts.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No shifts available</p>
            ) : (
              <div className="space-y-4">
                {Object.entries(
                  shifts.reduce((acc, shift) => {
                    const dateKey = new Date(shift.start_time).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      month: 'short', 
                      day: 'numeric' 
                    });
                    if (!acc[dateKey]) acc[dateKey] = [];
                    acc[dateKey].push(shift);
                    return acc;
                  }, {} as Record<string, typeof shifts>)
                ).map(([date, shiftsOnDate]: any[]) => (
                  <div key={date} className="mb-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b">{date}</h3>
                    <div className="space-y-3">
                      {(shiftsOnDate as typeof shifts).map((shift) => {
                        // Times from API are already in configured timezone
                        const startParts = shift.start_time.substring(0, 16).split('T');
                        const endParts = shift.end_time.substring(0, 16).split('T');
                        const startTimeStr = startParts[1];
                        const endTimeStr = endParts[1];
                        const isNextDay = startParts[0] !== endParts[0];
                        const totalMaxSpots = getTotalRoleSpots(shift);
                        const totalCurrentCount = getTotalRoleBooked(shift);
                        const isExpanded = expandedShifts.has(shift.id);
                        
                        // Calculate attendance stats
                        const volunteers = assignmentsByShift[shift.id] || [];
                        const presentCount = volunteers.filter((v: any) => v.attendance_status === 'present').length;
                        const lateCount = volunteers.filter((v: any) => v.attendance_status === 'late').length;
                        const absentCount = volunteers.filter((v: any) => v.attendance_status === 'absent').length;
                        const pendingCount = volunteers.filter((v: any) => !v.attendance_status || v.attendance_status === 'pending').length;

                        const totalFillPercentage = totalMaxSpots > 0 ? (totalCurrentCount / totalMaxSpots) * 100 : 0;
                        
                        return (
                          <div key={shift.id} className="border-2 border-gray-300 rounded-lg p-4 bg-white">
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <h4 className="font-bold text-gray-900">
                                    {globalSettings.show_shift_title && shift.title ? `${shift.title} · ` : ''}{startTimeStr} - {endTimeStr}{isNextDay && ' (next day)'}
                                  </h4>
                                  {shift.manual_full && (
                                    <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-700 border border-red-200">
                                      Manually full
                                    </span>
                                  )}
                                </div>
                                {globalSettings.show_shift_location && (
                                  <p className="text-sm text-gray-600">{shift.location || 'Meeting point TBD'}</p>
                                )}
                                {shift.tags && shift.tags.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {shift.tags.map((tag: any) => (
                                      <span key={tag.id} className={`text-xs px-2 py-0.5 rounded font-medium ${getTagColor(tag.name)}`}>
                                        {tag.name}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="text-right flex items-start gap-3">
                                <div>
                                  <div className="space-y-1">
                                    {shift.role_requirements && shift.role_requirements.map((req: any, idx: number) => (
                                      <div key={idx} className="text-xs text-right">
                                        <span className="font-medium capitalize">{req.role_name}s:</span>{' '}
                                        <span className={`text-sm font-bold ${
                                          (req.current_count || 0) < req.max_count * (globalSettings.fill_threshold_critical / 100) ? 'text-red-700' :
                                          (req.current_count || 0) < req.max_count * (globalSettings.fill_threshold_warning / 100) ? 'text-orange-700' :
                                          (req.current_count || 0) < req.max_count * (globalSettings.fill_threshold_low / 100) ? 'text-yellow-700' :
                                          'text-green-700'
                                        }`}>{req.current_count || 0}/{req.max_count}</span>
                                      </div>
                                    ))}
                                    <p className="text-xs text-gray-600 mt-1">
                                      Total: {totalCurrentCount}/{totalMaxSpots} ({Math.round(totalFillPercentage)}% full)
                                    </p>
                                    {volunteers.length > 0 && (new Date(shift.start_time) < new Date() || volunteers.some((v: any) => v.attendance_status && v.attendance_status !== 'pending')) && (
                                      <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                                        <div className="flex gap-2">
                                          <span className="text-green-700">✓ {presentCount}</span>
                                          <span className="text-yellow-700">⏱ {lateCount}</span>
                                          <span className="text-red-700">✗ {absentCount}</span>
                                          <span className="text-gray-500">◯ {pendingCount}</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <button
                                  onClick={() => toggleShiftExpanded(shift.id)}
                                  className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                                >
                                  {isExpanded ? '▼ Hide' : '▶ Show Details'}
                                </button>
                              </div>
                            </div>
                            
                            {isExpanded && (
                              <>
                            <div className="border-t pt-3 mb-3">
                              <p className="text-sm font-medium text-gray-700 mb-2">Shift Notes:</p>
                              <div className="flex gap-2">
                                <textarea
                                  value={editingShiftNotes[shift.id] ?? shift.supervisor_notes ?? ''}
                                  onChange={(e) => handleShiftNotesChange(shift.id, e.target.value)}
                                  className="flex-1 border rounded px-3 py-2 text-sm min-h-[60px] focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  placeholder="Add notes about this shift (visible to supervisors and admins)..."
                                />
                                {(editingShiftNotes[shift.id] !== undefined && 
                                  editingShiftNotes[shift.id] !== (shift.supervisor_notes || '')) && (
                                  <button
                                    onClick={() => handleSaveShiftNotes(shift.id)}
                                    className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium"
                                  >
                                    Save
                                  </button>
                                )}
                              </div>
                            </div>
                            
                            <div className="border-t pt-3">
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-sm font-medium text-gray-700">Assigned Volunteers:</p>
                                <button
                                  onClick={() => openAddModal(shift.id)}
                                  className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                                >
                                  Add Volunteer
                                </button>
                              </div>
                              {assignmentsByShift[shift.id] && assignmentsByShift[shift.id].length > 0 ? (
                                <div className="space-y-3">
                                  {/* Group volunteers by role */}
                                  {['volunteer', 'supervisor', 'admin'].map((roleType) => {
                                    const volunteersOfRole = assignmentsByShift[shift.id].filter((v: any) => v.role === roleType);
                                    if (volunteersOfRole.length === 0) return null;
                                    
                                    const roleColor = roleType === 'volunteer' ? 'bg-orange-50 border-orange-200' :
                                                     roleType === 'supervisor' ? 'bg-yellow-50 border-yellow-200' :
                                                     'bg-blue-50 border-blue-200';
                                    const roleTextColor = roleType === 'volunteer' ? 'text-orange-700' :
                                                         roleType === 'supervisor' ? 'text-yellow-700' :
                                                         'text-blue-700';
                                    
                                    return (
                                      <div key={roleType} className={`border rounded-lg p-3 ${roleColor}`}>
                                        <p className={`text-xs font-semibold uppercase mb-2 ${roleTextColor}`}>
                                          {roleType}s ({volunteersOfRole.length})
                                        </p>
                                        <div className="space-y-2">
                                          {volunteersOfRole.map((v: any) => (
                                            <div key={v.booking_id} className="flex items-center justify-between bg-white rounded border p-2">
                                              <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                  <p className="font-medium text-gray-900">{getUserDisplayName(v.id, v.name, v.email)}</p>
                                                  {v.attendance_status === 'present' && (
                                                    <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs font-medium">
                                                      ✓ Present
                                                    </span>
                                                  )}
                                                  {v.attendance_status === 'late' && (
                                                    <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded text-xs font-medium">
                                                      ⏱ Late{v.late_by_minutes ? ` (${v.late_by_minutes}m)` : ''}
                                                    </span>
                                                  )}
                                                  {v.attendance_status === 'absent' && (
                                                    <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs font-medium">
                                                      ✗ Absent
                                                    </span>
                                                  )}
                                                </div>
                                                <p className="text-xs text-gray-600">{v.email}</p>
                                              </div>
                                              <div className="flex gap-2">
                                                <button
                                                  onClick={() => openReassignModal(v.booking_id)}
                                                  className="text-blue-600 hover:text-blue-800 text-sm"
                                                >
                                                  Reassign
                                                </button>
                                                <button
                                                  onClick={() => openUserShiftsModal(v.id, v.name || v.email)}
                                                  className="text-gray-700 hover:text-gray-900 text-sm"
                                                >
                                                  View Shifts
                                                </button>
                                                <button
                                                  onClick={() => handleUnassignVolunteer(v.booking_id)}
                                                  className="text-red-600 hover:text-red-800 text-sm"
                                                >
                                                  Unassign
                                                </button>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-500 italic">No volunteers assigned yet</p>
                              )}
                            </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
        )}

        {/* Add Volunteer Modal */}
        {showAddModal && addTargetShiftId !== null && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white w-full max-w-2xl rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Add Volunteers to Shift #{addTargetShiftId}</h3>
                <button onClick={closeAddModal} className="text-gray-600 hover:text-gray-800">Close</button>
              </div>
              <input
                placeholder="Search users..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="w-full border rounded px-3 py-2 mb-3"
              />
              <div className="max-h-64 overflow-y-auto border rounded">
                {users
                  .filter((u) => u.application_status === 'approved')
                  .filter((u) => !u.withdrawn)
                  .filter((u) => (u.full_name || u.name || u.email).toLowerCase().includes(userSearch.toLowerCase()))
                  .map((u) => (
                    <label key={u.id} className="flex items-center justify-between px-3 py-2 border-b last:border-0">
                      <div>
                        <p className="font-medium text-gray-900">{u.full_name || u.name || u.email}</p>
                        <p className="text-xs text-gray-600">{u.email} · {u.role}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(u.id)}
                        onChange={() => toggleUserSelection(u.id)}
                        className="w-5 h-5"
                      />
                    </label>
                  ))}
                {users.filter((u) => u.application_status === 'approved' && !u.withdrawn).length === 0 && (
                  <p className="text-gray-600 p-3">No approved users found</p>
                )}
              </div>
              <div className="flex items-center justify-end gap-3 mt-4">
                <button onClick={closeAddModal} className="px-4 py-2 bg-gray-200 text-gray-800 rounded">Cancel</button>
                <button onClick={confirmAddVolunteers} className="px-4 py-2 bg-blue-600 text-white rounded">Add Selected</button>
              </div>
            </div>
          </div>
        )}

        {showSwapModal && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white w-full max-w-6xl rounded-lg shadow-lg p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <h3 className="text-lg font-semibold">Swap Shifts</h3>
                  <p className="text-sm text-gray-600 mt-1">Pick two volunteers, then select one shift for each volunteer to exchange.</p>
                </div>
                <button onClick={closeSwapModal} className="text-gray-600 hover:text-gray-800">Close</button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="border rounded-lg p-4 bg-gray-50">
                  <h4 className="font-semibold text-gray-900 mb-3">1. Select Volunteer 1</h4>
                  <input
                    placeholder="Search volunteers..."
                    value={swapVolunteer1Search}
                    onChange={(e) => setSwapVolunteer1Search(e.target.value)}
                    className="w-full border rounded px-3 py-2 mb-3"
                  />
                  <div className="max-h-64 overflow-y-auto border rounded bg-white">
                    {filteredSwapCandidates1.length > 0 ? filteredSwapCandidates1.map((candidate) => (
                      <button
                        key={candidate.id}
                        type="button"
                        onClick={() => {
                          setSwapVolunteer1Id(candidate.id);
                          setSwapBooking1Id(null);
                          setSwapVolunteer2Id(null);
                          setSwapBooking2Id(null);
                        }}
                        className={`w-full text-left px-3 py-2 border-b last:border-0 hover:bg-gray-50 ${swapVolunteer1Id === candidate.id ? 'bg-indigo-50' : ''}`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="font-medium text-gray-900">{candidate.name}</p>
                            <p className="text-xs text-gray-600">{candidate.email} · {candidate.bookings.length} shift{candidate.bookings.length === 1 ? '' : 's'}</p>
                          </div>
                          {swapVolunteer1Id === candidate.id && <span className="text-indigo-700 text-xs font-semibold">Selected</span>}
                        </div>
                      </button>
                    )) : (
                      <p className="p-3 text-sm text-gray-500">No volunteers with shifts found</p>
                    )}
                  </div>

                  {selectedSwapVolunteer1 && (
                    <div className="mt-4">
                      <h5 className="text-sm font-semibold text-gray-800 mb-2">Volunteer 1 shifts</h5>
                      <div className="space-y-2">
                        {selectedSwapVolunteer1.bookings.map((booking: any) => (
                          <button
                            key={booking.booking_id}
                            type="button"
                            onClick={() => setSwapBooking1Id(booking.booking_id)}
                            className={`w-full text-left border rounded px-3 py-2 hover:bg-white ${swapBooking1Id === booking.booking_id ? 'border-indigo-500 bg-indigo-50' : 'bg-white'}`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="font-medium text-gray-900">{formatAdminShiftLabel(booking.shift)}</p>
                                <p className="text-xs text-gray-600">Role: {booking.role}</p>
                              </div>
                              {swapBooking1Id === booking.booking_id && <span className="text-indigo-700 text-xs font-semibold">Chosen</span>}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="border rounded-lg p-4 bg-gray-50">
                  <h4 className="font-semibold text-gray-900 mb-3">2. Select Volunteer 2</h4>
                  <input
                    placeholder="Search volunteers..."
                    value={swapVolunteer2Search}
                    onChange={(e) => setSwapVolunteer2Search(e.target.value)}
                    className="w-full border rounded px-3 py-2 mb-3"
                  />
                  <div className="max-h-64 overflow-y-auto border rounded bg-white">
                    {filteredSwapCandidates2.length > 0 ? filteredSwapCandidates2.map((candidate) => (
                      <button
                        key={candidate.id}
                        type="button"
                        onClick={() => {
                          setSwapVolunteer2Id(candidate.id);
                          setSwapBooking2Id(null);
                        }}
                        className={`w-full text-left px-3 py-2 border-b last:border-0 hover:bg-gray-50 ${swapVolunteer2Id === candidate.id ? 'bg-indigo-50' : ''}`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="font-medium text-gray-900">{candidate.name}</p>
                            <p className="text-xs text-gray-600">{candidate.email} · {candidate.bookings.length} shift{candidate.bookings.length === 1 ? '' : 's'}</p>
                          </div>
                          {swapVolunteer2Id === candidate.id && <span className="text-indigo-700 text-xs font-semibold">Selected</span>}
                        </div>
                      </button>
                    )) : (
                      <p className="p-3 text-sm text-gray-500">No volunteers with shifts found</p>
                    )}
                  </div>

                  {selectedSwapVolunteer2 && (
                    <div className="mt-4">
                      <h5 className="text-sm font-semibold text-gray-800 mb-2">Volunteer 2 shifts</h5>
                      <div className="space-y-2">
                        {selectedSwapVolunteer2.bookings.map((booking: any) => (
                          <button
                            key={booking.booking_id}
                            type="button"
                            onClick={() => setSwapBooking2Id(booking.booking_id)}
                            className={`w-full text-left border rounded px-3 py-2 hover:bg-white ${swapBooking2Id === booking.booking_id ? 'border-indigo-500 bg-indigo-50' : 'bg-white'}`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="font-medium text-gray-900">{formatAdminShiftLabel(booking.shift)}</p>
                                <p className="text-xs text-gray-600">Role: {booking.role}</p>
                              </div>
                              {swapBooking2Id === booking.booking_id && <span className="text-indigo-700 text-xs font-semibold">Chosen</span>}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-5 border-t pt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="text-sm text-gray-700 space-y-1">
                  <p><span className="font-medium">Volunteer 1:</span> {selectedSwapVolunteer1 ? selectedSwapVolunteer1.name : 'Not selected'} {selectedSwapBooking1 ? `→ ${formatAdminShiftLabel(selectedSwapBooking1.shift)}` : ''}</p>
                  <p><span className="font-medium">Volunteer 2:</span> {selectedSwapVolunteer2 ? selectedSwapVolunteer2.name : 'Not selected'} {selectedSwapBooking2 ? `→ ${formatAdminShiftLabel(selectedSwapBooking2.shift)}` : ''}</p>
                </div>
                <div className="flex gap-2 justify-end">
                  <button onClick={closeSwapModal} className="px-4 py-2 bg-gray-200 text-gray-800 rounded">
                    Cancel
                  </button>
                  <button
                    onClick={handleSwapAssignments}
                    disabled={!selectedSwapBooking1 || !selectedSwapBooking2 || swappingAssignments}
                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {swappingAssignments ? 'Swapping...' : 'Swap Shifts'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reassign Modal */}
        {showReassignModal && reassignBookingId !== null && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white w-full max-w-xl rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">{reassignContext === 'sick-note' ? 'Reschedule sick volunteer' : 'Reassign Volunteer'}</h3>
                <button onClick={closeReassignModal} className="text-gray-600 hover:text-gray-800">Close</button>
              </div>
              {reassignContext === 'sick-note' && (
                <p className="text-sm text-gray-600 mb-3">Pick a replacement shift for this sick note before marking it accepted.</p>
              )}
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Target Shift</label>
              <select
                value={reassignTargetShiftId === '' ? '' : String(reassignTargetShiftId)}
                onChange={(e) => setReassignTargetShiftId(e.target.value === '' ? '' : Number(e.target.value))}
                className="w-full border rounded px-3 py-2 mb-4"
              >
                <option value="">— Choose —</option>
                {shifts.map((s) => {
                  const start = new Date(s.start_time);
                  const end = new Date(s.end_time);
                  const isNextDay = start.toDateString() !== end.toDateString();
                  const fmt = (d: Date) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
                  const dateLabel = start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                  const isOriginalShift = reassignContext === 'sick-note' && s.id === reassignOriginalShiftId;
                  return (
                    <option key={s.id} value={s.id} disabled={isOriginalShift}>
                      {dateLabel} · {fmt(start)}–{fmt(end)}{isNextDay ? ' (next day)' : ''} · {s.title}{isOriginalShift ? ' (original shift)' : ''}
                    </option>
                  );
                })}
              </select>
              <div className="flex items-center justify-end gap-3">
                <button onClick={closeReassignModal} className="px-4 py-2 bg-gray-200 text-gray-800 rounded">Cancel</button>
                <button onClick={confirmReassign} className="px-4 py-2 bg-blue-600 text-white rounded">{reassignContext === 'sick-note' ? 'Confirm reschedule' : 'Confirm Reassign'}</button>
              </div>
            </div>
          </div>
        )}

        {/* User Shifts Modal */}
        {showUserShiftsModal && modalUserId !== null && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white w-full max-w-2xl rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Shifts for {modalUserName}</h3>
                <button onClick={closeUserShiftsModal} className="text-gray-600 hover:text-gray-800">Close</button>
              </div>
              {modalUserShifts.length === 0 ? (
                <p className="text-gray-600">No shifts for this user.</p>
              ) : (
                <div className="space-y-2">
                  {modalUserShifts.map((b) => {
                    const start = new Date(b.start_time);
                    const end = new Date(b.end_time);
                    const isNextDay = start.toDateString() !== end.toDateString();
                    const fmt = (d: Date) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
                    const dateLabel = start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                    const attendanceStatus = b.attendance_status || 'pending';
                    const statusColors: Record<string, string> = {
                      present: 'bg-green-100 text-green-800',
                      late: 'bg-yellow-100 text-yellow-800',
                      absent: 'bg-red-100 text-red-800',
                      pending: 'bg-gray-100 text-gray-600',
                    };
                    const statusLabels: Record<string, string> = {
                      present: '✓ Present',
                      late: `⏱ Late${b.late_by_minutes ? ` (${b.late_by_minutes} min)` : ''}`,
                      absent: '✗ Absent',
                      pending: '◯ Pending',
                    };
                    return (
                      <div key={b.booking_id} className="border rounded p-3 bg-gray-50">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{dateLabel} · {fmt(start)}–{fmt(end)}{isNextDay ? ' (next day)' : ''}</p>
                            <p className="text-xs text-gray-600">{b.title} · {b.location || 'Meeting point TBD'}</p>
                            {(attendanceStatus !== 'pending' || new Date(b.start_time) < new Date()) && (
                              <div className="flex items-center gap-2 mt-1">
                                <span className={`text-xs px-2 py-0.5 rounded ${statusColors[attendanceStatus]}`}>
                                  {statusLabels[attendanceStatus]}
                                </span>
                              </div>
                            )}
                            {b.supervisor_notes && (
                              <p className="text-xs text-gray-700 mt-1 italic">Note: {b.supervisor_notes}</p>
                            )}
                          </div>
                          <button
                            onClick={() => handleUnassignVolunteer(b.booking_id)}
                            className="text-red-600 hover:text-red-800 text-sm ml-2 flex-shrink-0"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* User Withdrawal Confirmation Modal */}
        {showUserWithdrawalModal && withdrawingUserId !== null && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white w-full max-w-2xl rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-red-600">Withdraw Volunteer</h3>
                <button onClick={() => setShowUserWithdrawalModal(false)} className="text-gray-600 hover:text-gray-800">Close</button>
              </div>
              <p className="text-gray-700 mb-4">
                Are you sure you want to withdraw <strong>{withdrawingUserName}</strong>? This will remove them from all their assigned shifts:
              </p>
              {withdrawingUserShifts.length === 0 ? (
                <p className="text-gray-600 mb-6 italic">No shifts assigned.</p>
              ) : (
                <div className="space-y-2 mb-6 max-h-64 overflow-y-auto">
                  {withdrawingUserShifts.map((b) => {
                    const start = new Date(b.start_time);
                    const end = new Date(b.end_time);
                    const isNextDay = start.toDateString() !== end.toDateString();
                    const fmt = (d: Date) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
                    const dateLabel = start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                    return (
                      <div key={b.booking_id} className="flex items-center justify-between border rounded p-3 bg-red-50">
                        <div>
                          <p className="font-medium text-gray-900">{dateLabel} · {fmt(start)}–{fmt(end)}{isNextDay ? ' (next day)' : ''}</p>
                          <p className="text-xs text-gray-600">{b.title} · {b.location || 'Meeting point TBD'}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="flex items-center justify-end gap-3">
                <button 
                  onClick={() => setShowUserWithdrawalModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmWithdrawUser}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Confirm Withdrawal
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Withdrawal Confirmation Modal */}
        {showWithdrawalModal && withdrawalSickNoteId !== null && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white w-full max-w-2xl rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Confirm Volunteer Withdrawal</h3>
                <button onClick={() => setShowWithdrawalModal(false)} className="text-gray-600 hover:text-gray-800">Close</button>
              </div>
              <div className="mb-4">
                <p className="text-gray-800 mb-2">
                  <strong>{withdrawalVolunteerName}</strong> will be withdrawn from the festival.
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  The following {withdrawalShifts.length} future shift{withdrawalShifts.length !== 1 ? 's' : ''} will be permanently removed:
                </p>
              </div>
              {withdrawalShifts.length === 0 ? (
                <p className="text-gray-600 mb-4">No future shifts to remove.</p>
              ) : (
                <div className="space-y-2 mb-4 max-h-96 overflow-y-auto">
                  {withdrawalShifts.map((shift) => {
                    const start = new Date(shift.start_time);
                    const end = new Date(shift.end_time);
                    const isNextDay = start.toDateString() !== end.toDateString();
                    const fmt = (d: Date) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
                    const dateLabel = start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                    return (
                      <div key={shift.booking_id} className="border rounded p-3 bg-red-50">
                        <p className="font-medium text-gray-900">{dateLabel} · {fmt(start)}–{fmt(end)}{isNextDay ? ' (next day)' : ''}</p>
                        <p className="text-sm text-gray-600">{shift.title}{shift.location ? ` · ${shift.location}` : ''}</p>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="flex items-center justify-end gap-3">
                <button onClick={() => setShowWithdrawalModal(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded">Cancel</button>
                <button onClick={confirmWithdrawal} className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">
                  Confirm Withdrawal
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
        <div className="grid grid-cols-1 gap-6">
          <section className="bg-white shadow rounded p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Users</h2>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowUserImportForm(!showUserImportForm)}
                  className="text-sm px-3 py-1 rounded font-medium transition-colors bg-blue-50 text-blue-700 hover:bg-blue-100"
                >
                  {showUserImportForm ? 'Hide Import' : 'Show Import'}
                </button>
                <button
                  onClick={() => setShowInactiveUsers(!showInactiveUsers)}
                  className={`text-sm px-3 py-1 rounded font-medium transition-colors ${
                    showInactiveUsers
                      ? 'bg-gray-400 text-white hover:bg-gray-500'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {showInactiveUsers ? 'Hide Inactive' : 'Show Inactive'}
                </button>
                <button
                  onClick={() => setShowNotOnWhatsAppOnly(!showNotOnWhatsAppOnly)}
                  className={`text-sm px-3 py-1 rounded font-medium transition-colors ${
                    showNotOnWhatsAppOnly
                      ? 'bg-amber-500 text-white hover:bg-amber-600'
                      : 'bg-amber-50 text-amber-800 hover:bg-amber-100'
                  }`}
                >
                  {showNotOnWhatsAppOnly ? 'Show All WhatsApp Status' : 'Not on WhatsApp Only'}
                </button>
                <button
                  onClick={() => setShowWithoutShiftsOnly(!showWithoutShiftsOnly)}
                  className={`text-sm px-3 py-1 rounded font-medium transition-colors ${
                    showWithoutShiftsOnly
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-green-50 text-green-800 hover:bg-green-100'
                  }`}
                >
                  {showWithoutShiftsOnly ? 'Show All Users' : 'Only Without Shifts'}
                </button>
                <button
                  onClick={() => setShowReminderModal(true)}
                  className="text-sm px-3 py-1 rounded font-medium transition-colors bg-blue-600 text-white hover:bg-blue-700"
                >
                  Send Reminder
                </button>
                <span className="text-sm text-gray-500">
                  {
                    users
                      .filter(u => showInactiveUsers || (u.application_status !== 'rejected' && !u.withdrawn))
                      .filter(u => !showNotOnWhatsAppOnly || !!u.not_found_on_whatsapp)
                      .filter(u => !showWithoutShiftsOnly || (Number(u.shifts_booked || 0) === 0))
                      .length
                  } displayed
                </span>
              </div>
            </div>

            <div className="mb-3 text-xs text-gray-500">
              {showWithoutShiftsOnly ? 'Filtering: users without any booked shifts' : (showNotOnWhatsAppOnly ? 'Filtering: users marked as not found on WhatsApp' : 'Filtering: all WhatsApp statuses')}
            </div>

            {(['admin', 'supervisor', 'volunteer', 'guest'] as const).map((roleType) => {
              const roleUsers = users
                .filter(u => u.role === roleType)
                .filter(u => showInactiveUsers || (u.application_status !== 'rejected' && !u.withdrawn))
                .filter(u => !showNotOnWhatsAppOnly || !!u.not_found_on_whatsapp)
                .filter(u => !showWithoutShiftsOnly || (Number(u.shifts_booked || 0) === 0))
                .sort((a, b) => {
                  const aName = (a.full_name || a.name || a.email || '').toLowerCase();
                  const bName = (b.full_name || b.name || b.email || '').toLowerCase();
                  return aName.localeCompare(bName);
                });
              
              if (roleUsers.length === 0) return null;

              return (
                <div key={roleType} className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 bg-gray-50 px-4 py-2 rounded mb-2 uppercase tracking-wide">
                    {roleType.charAt(0).toUpperCase() + roleType.slice(1)}s ({roleUsers.length})
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-600 border-b">
                          <th className="py-2 pr-4">Name</th>
                          <th className="py-2 pr-4">Email</th>
                          <th className="py-2 pr-4">Phone</th>
                          <th className="py-2 pr-4">Shifts</th>
                          {roleType === 'guest' && <th className="py-2 pr-4">Status</th>}
                          <th className="py-2">Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {roleUsers.map((u) => (
                          <tr key={u.id} className="border-b last:border-0">
                            <td className="py-2 pr-4 font-medium">
                              <Link
                                to={`/admin/users/${u.id}`}
                                className="text-blue-600 hover:underline"
                              >
                                {u.full_name || u.name || '—'}
                              </Link>
                            </td>
                            <td className="py-2 pr-4 text-gray-700">{u.email}</td>
                            <td className="py-2 pr-4">{formatPhone(u.phone)}</td>
                            <td className="py-2 pr-4">
                              <div className="flex flex-col text-xs">
                                <span className="font-medium">
                                  {u.shifts_booked || 0} booked
                                </span>
                                <span className="text-green-600">
                                  ✓ {u.shifts_attended || 0} attended
                                </span>
                                {(u.shifts_missed || 0) > 0 && (
                                  <span className="text-red-600">
                                    ✗ {u.shifts_missed || 0} missed
                                  </span>
                                )}
                                {(u.shifts_unexcused || 0) > 0 && (
                                  <span className="text-red-700">
                                    ✗ {u.shifts_unexcused || 0} unexcused
                                  </span>
                                )}
                                {(u.shifts_excused || 0) > 0 && (
                                  <span className="text-blue-700">
                                    ✗ {u.shifts_excused || 0} excused
                                  </span>
                                )}
                                {(u.shifts_late || 0) > 0 && (
                                  <span className="text-yellow-600">
                                    ⏱ {u.shifts_late || 0} late
                                  </span>
                                )}
                              </div>
                            </td>
                            {roleType === 'guest' && (
                              <td className="py-2 pr-4">
                                {u.withdrawn ? (
                                  <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                    Withdrawn
                                  </span>
                                ) : u.application_status ? (
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    u.application_status === 'approved' ? 'bg-green-100 text-green-800' :
                                    u.application_status === 'rejected' ? 'bg-red-100 text-red-800' :
                                    'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {u.application_status}
                                  </span>
                                ) : (
                                  <span className="text-gray-500">—</span>
                                )}
                              </td>
                            )}
                            <td className="py-2">
                              <Link
                                to={`/admin/users/${u.id}`}
                                className="text-blue-600 hover:underline"
                              >
                                View details
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}

            {showUserImportForm && (
              <div className="mt-8 border rounded-lg p-4 bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-800 mb-2">Import users from CSV</h3>
                <p className="text-xs text-gray-600 mb-3">Expected headers: Name;DOB;Role;People-Vol Registration;Phone (WhatsApp);Email;Days since Invite;Referred by;Are you from DTU?</p>
                <div className="flex flex-col gap-3">
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleUserImportFile}
                    className="text-sm"
                  />
                  <textarea
                    rows={6}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="Paste CSV content here"
                    value={userImportText}
                    onChange={(e) => setUserImportText(e.target.value)}
                  />
                  {userImportError && <p className="text-sm text-red-600">{userImportError}</p>}
                  {userImportResult && (
                    <div className="text-sm text-gray-700">
                      Imported: {userImportResult.created} created, {userImportResult.updated} updated, {userImportResult.skipped} skipped
                      {userImportResult.errors?.length > 0 && (
                        <ul className="mt-2 list-disc list-inside text-xs text-red-600">
                          {userImportResult.errors.slice(0, 10).map((err, idx) => (
                            <li key={idx}>{err}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={handleImportUsers}
                      disabled={userImportLoading}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                    >
                      {userImportLoading ? 'Importing...' : 'Import Users'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setUserImportText('');
                        setUserImportError('');
                        setUserImportResult(null);
                      }}
                      className="px-3 py-2 text-sm bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Reminder modal */}
            {showReminderModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                <div className="w-full max-w-2xl rounded-2xl border bg-white p-6 shadow-2xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Send reminder to users without shifts</h3>
                    <button onClick={() => setShowReminderModal(false)} className="text-gray-600 hover:text-gray-800">Close</button>
                  </div>
                  <div className="space-y-3">
                    <label className="text-sm font-medium">Subject</label>
                    <input className="w-full border rounded px-3 py-2" value={reminderSubject} onChange={(e) => setReminderSubject(e.target.value)} />
                    <label className="text-sm font-medium">Message template</label>
                    <textarea rows={8} className="w-full border rounded px-3 py-2 font-mono text-sm" value={reminderTemplate} onChange={(e) => setReminderTemplate(e.target.value)} />
                    <p className="text-xs text-gray-500">You can use placeholders: %event%, %firstname%, %frontend_url%</p>
                    <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-semibold text-blue-900">Recipients</p>
                        <p className="text-sm text-blue-900">{reminderRecipients.length} user{reminderRecipients.length === 1 ? '' : 's'}</p>
                      </div>
                      {reminderRecipients.length === 0 ? (
                        <p className="mt-2 text-sm text-blue-800">No active volunteers, supervisors, or admins without shifts were found.</p>
                      ) : (
                        <div className="mt-3 max-h-48 overflow-y-auto space-y-2">
                          {reminderRecipients.slice(0, 20).map((recipient) => (
                            <div key={recipient.id} className="flex items-center justify-between gap-3 rounded bg-white px-3 py-2 text-sm shadow-sm">
                              <div className="min-w-0">
                                <p className="font-medium text-gray-900 truncate">{recipient.full_name || recipient.name || '—'}</p>
                                <p className="text-gray-600 truncate">{recipient.email}</p>
                              </div>
                              <span className="shrink-0 rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
                                {recipient.role}
                              </span>
                            </div>
                          ))}
                          {reminderRecipients.length > 20 && (
                            <p className="text-xs text-blue-800">Showing first 20 recipients.</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-3 mt-4">
                    <button onClick={() => setShowReminderModal(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded">Cancel</button>
                    <button
                      onClick={async () => {
                        if (!reminderSubject.trim() || !reminderTemplate.trim()) {
                          alert('Please provide subject and message');
                          return;
                        }
                        if (!confirm('Send reminder to all active volunteers/supervisors/admins without any booked shifts?')) return;
                        try {
                          setSendingReminder(true);
                          const resp = await adminService.sendNoShiftsReminder({ subject: reminderSubject, template: reminderTemplate });
                          alert(`Sent ${resp.data.sent} of ${resp.data.total_targets} emails`);
                          setShowReminderModal(false);
                        } catch (err: any) {
                          alert(err.response?.data?.error || 'Failed to send reminders');
                        } finally {
                          setSendingReminder(false);
                        }
                      }}
                      disabled={sendingReminder || reminderRecipients.length === 0}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60"
                    >
                      {sendingReminder ? 'Sending...' : 'Send Reminders'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
        )}

        {activeTab === 'constraints' && (
        <div className="grid grid-cols-1 gap-6">
          {/* Create New Constraint Card */}
          <section className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-bold mb-6 text-gray-900">Create New Constraint</h2>
            
            <div className="space-y-4 max-w-2xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Constraint Name *</label>
                  <input
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., 'Min night shifts'"
                    value={constraintForm.name}
                    onChange={(e) => setConstraintForm((p) => ({ ...p, name: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description (optional)</label>
                <textarea
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 'Volunteers must work at least 2 night shifts'"
                  rows={2}
                  value={constraintForm.description}
                  onChange={(e) => setConstraintForm((p) => ({ ...p, description: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Apply to Role (optional)</label>
                  <select
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={constraintForm.role_id}
                    onChange={(e) => setConstraintForm((p) => ({ ...p, role_id: e.target.value === '' ? '' : Number(e.target.value) }))}
                  >
                    <option value="">All roles</option>
                    {roles.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Apply to Tag (optional)</label>
                  <select
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={constraintForm.tag_id}
                    onChange={(e) => setConstraintForm((p) => ({ ...p, tag_id: e.target.value === '' ? '' : Number(e.target.value) }))}
                  >
                    <option value="">All tags</option>
                    {tags.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Constraint Type (optional)</label>
                  <div className="text-xs text-gray-500 mt-1">Choose one or more below</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Shifts</label>
                  <input
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    type="number"
                    min={0}
                    placeholder="e.g., 2"
                    value={constraintForm.min_shifts}
                    onChange={(e) => setConstraintForm((p) => ({ ...p, min_shifts: Number(e.target.value) || 0 }))}
                  />
                  <p className="text-xs text-gray-500 mt-1">Min shifts required (set to 0 to disable)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Maximum Shifts</label>
                  <input
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    type="number"
                    min={0}
                    placeholder="e.g., 5"
                    value={constraintForm.max_shifts}
                    onChange={(e) => setConstraintForm((p) => ({ ...p, max_shifts: Number(e.target.value) || 0 }))}
                  />
                  <p className="text-xs text-gray-500 mt-1">Max shifts allowed (set to 0 to disable)</p>
                </div>
              </div>

              <button
                onClick={handleCreateConstraint}
                className="w-full md:w-auto bg-blue-600 text-white py-2 px-6 rounded-lg hover:bg-blue-700 font-medium"
              >
                Create Constraint
              </button>
            </div>
          </section>

          {/* Active Constraints */}
          <section className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Active Constraints ({constraints.length})</h2>
            </div>

            {constraints.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg">No constraints yet. Create one above to get started.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {constraints.map((c) => (
                  <div
                    key={c.id}
                    className={`border rounded-lg p-4 ${
                      c.enabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-gray-900">{c.name}</h3>
                          <span className={`text-xs px-2 py-1 rounded font-medium ${
                            c.enabled
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {c.enabled ? '✓ Active' : 'Inactive'}
                          </span>
                        </div>
                        
                        {c.description && (
                          <p className="text-sm text-gray-600 mb-2">{c.description}</p>
                        )}

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-sm">
                          <div>
                            <span className="text-gray-500">Role:</span>
                            <p className="font-medium">{c.role_name || 'All roles'}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Tag:</span>
                            <p className="font-medium">{c.tag_name || 'All tags'}</p>
                          </div>
                          {c.min_shifts !== undefined && c.min_shifts !== null && (
                            <div>
                              <span className="text-gray-500">Min shifts:</span>
                              <p className="font-medium">{c.min_shifts}</p>
                            </div>
                          )}
                          {c.max_shifts !== undefined && c.max_shifts !== null && (
                            <div>
                              <span className="text-gray-500">Max shifts:</span>
                              <p className="font-medium">{c.max_shifts}</p>
                            </div>
                          )}
                          {c.min_hours_between_shifts !== undefined && c.min_hours_between_shifts !== null && c.min_hours_between_shifts > 0 && (
                            <div>
                              <span className="text-gray-500">Min gap:</span>
                              <p className="font-medium">{c.min_hours_between_shifts}h</p>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3 flex-shrink-0">
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={c.enabled}
                            onChange={(e) => handleToggleConstraint(c.id, e.target.checked)}
                            className="w-5 h-5 rounded cursor-pointer"
                          />
                        </label>
                        <button
                          onClick={() => handleDeleteConstraint(c.id)}
                          className="text-red-600 hover:text-red-800 hover:bg-red-50 px-3 py-1 rounded text-sm font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
        )}

        {activeTab === 'deviations' && (
        <div className="grid grid-cols-1 gap-6">
          <section className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Attendance Deviations</h2>
                <p className="text-gray-600 mt-1">
                  Track and manage absences, late arrivals, and supervisor notes.
                </p>
              </div>
              <button
                onClick={() => setShowHandledDeviations(!showHandledDeviations)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium text-sm"
              >
                {showHandledDeviations ? 'Hide' : 'Show'} Handled ({deviations.filter(d => d.absence_handled).length})
              </button>
            </div>

            {deviations.filter(d => showHandledDeviations || !d.absence_handled).length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                {showHandledDeviations ? 'No deviations to review' : 'No unhandled deviations'}
              </p>
            ) : (
              <div className="space-y-4">
                {deviations.filter(d => showHandledDeviations || !d.absence_handled).map((deviation) => {
                  const isShiftDeviation = deviation.deviation_type === 'shift';
                  const isHandled = deviation.absence_handled;
                  const isAbsent = deviation.attendance_status === 'absent';
                  const isLate = deviation.attendance_status === 'late';
                  const isPresent = deviation.attendance_status === 'present';
                  const hasNotes = !!deviation.booking_notes || !!deviation.shift_notes;
                  
                  return (
                    <div
                      key={isShiftDeviation ? `shift-${deviation.shift_id}` : `booking-${deviation.booking_id}`}
                      className={`border rounded-lg p-4 ${
                        isHandled ? 'bg-gray-50 border-gray-300' : 'bg-white border-gray-200 shadow-sm'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            {isShiftDeviation ? (
                              <h3 className="font-semibold text-gray-900">
                                Shift deviation
                              </h3>
                            ) : (
                              <h3 
                                className="font-semibold text-gray-900 cursor-pointer hover:text-blue-600 hover:underline"
                                onClick={() => openUserShiftsModal(deviation.user_id, deviation.full_name || deviation.name || deviation.email)}
                              >
                                {deviation.full_name || deviation.name || deviation.email}
                              </h3>
                            )}
                            {isAbsent && (
                              <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-medium">
                                Absent
                              </span>
                            )}
                            {isLate && (
                              <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-medium">
                                Late
                              </span>
                            )}
                            {isPresent && (
                              <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                                Present
                              </span>
                            )}
                            {deviation.is_running && (
                              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                                Running
                              </span>
                            )}
                            {isHandled && (
                              <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                                ✓ Handled
                              </span>
                            )}
                            {!isShiftDeviation && deviation.absence_excused !== null && (
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                deviation.absence_excused
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-orange-100 text-orange-800'
                              }`}>
                                {deviation.absence_excused ? 'Excused' : 'Unexcused'}
                              </span>
                            )}
                          </div>

                          <div className="text-sm text-gray-600 space-y-1">
                            <p>
                              <span className="font-medium">Shift:</span> {deviation.shift_title || 'Untitled'}
                            </p>
                            <p>
                              <span className="font-medium">Time:</span>{' '}
                              {new Date(deviation.start_time).toLocaleString('en-GB', { hour12: false })} –{' '}
                              {new Date(deviation.end_time).toLocaleString('en-GB', { hour12: false })}
                            </p>
                            {deviation.location && <p><span className="font-medium">Location:</span> {deviation.location}</p>}
                            {!isShiftDeviation && deviation.phone && <p><span className="font-medium">Phone:</span> {deviation.phone}</p>}
                            {!isShiftDeviation && deviation.email && <p><span className="font-medium">Email:</span> {deviation.email}</p>}
                            {!isShiftDeviation && deviation.late_by_minutes && (
                              <p className="text-yellow-700 font-medium">
                                ⏱ Late by {deviation.late_by_minutes} minutes
                              </p>
                            )}
                          </div>

                          {hasNotes && (
                            <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded p-3">
                              {deviation.shift_notes && (
                                <div className={deviation.booking_notes ? "mb-2" : ""}>
                                  <p className="text-xs font-semibold text-yellow-900 mb-1">Shift Notes (Admin/Supervisor):</p>
                                  <p className="text-sm text-yellow-800 whitespace-pre-wrap">{deviation.shift_notes}</p>
                                </div>
                              )}
                              {deviation.booking_notes && (
                                <div>
                                  <p className="text-xs font-semibold text-yellow-900 mb-1">Volunteer Notes (Supervisor):</p>
                                  <p className="text-sm text-yellow-800 whitespace-pre-wrap">{deviation.booking_notes}</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="flex flex-col gap-2">
                          {isShiftDeviation ? (
                            !isHandled ? (
                              <button
                                onClick={async () => {
                                  await adminService.updateShiftDeviation(deviation.shift_id, { read: true });
                                  await loadAll();
                                }}
                                className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 text-sm font-medium"
                              >
                                Mark as Read
                              </button>
                            ) : (
                              <button
                                onClick={async () => {
                                  await adminService.updateShiftDeviation(deviation.shift_id, { read: false });
                                  await loadAll();
                                }}
                                className="px-3 py-1.5 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm font-medium"
                              >
                                Mark as Unread
                              </button>
                            )
                          ) : (
                            <>
                              {isAbsent && (
                                <>
                                  <button
                                    onClick={() => handleMarkDeviation(deviation.booking_id, true, true)}
                                    className={`px-3 py-1.5 rounded text-sm font-medium ${
                                      deviation.absence_excused === true
                                        ? 'bg-blue-700 text-white'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                    }`}
                                  >
                                    Mark Excused
                                  </button>
                                  <button
                                    onClick={() => handleMarkDeviation(deviation.booking_id, false, true)}
                                    className={`px-3 py-1.5 rounded text-sm font-medium ${
                                      deviation.absence_excused === false
                                        ? 'bg-orange-700 text-white'
                                        : 'bg-orange-600 text-white hover:bg-orange-700'
                                    }`}
                                  >
                                    Mark Unexcused
                                  </button>
                                  <button
                                    onClick={() => handleMarkDeviation(deviation.booking_id, null, false)}
                                    className="px-3 py-1.5 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 text-sm font-medium"
                                  >
                                    Reset Status
                                  </button>
                                </>
                              )}
                              {!isAbsent && (
                                !isHandled ? (
                                  <button
                                    onClick={() => handleMarkDeviation(deviation.booking_id, deviation.absence_excused, true)}
                                    className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 text-sm font-medium"
                                  >
                                    Mark as Handled
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleMarkDeviation(deviation.booking_id, deviation.absence_excused, false)}
                                    className="px-3 py-1.5 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm font-medium"
                                  >
                                    Un-handle
                                  </button>
                                )
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
        )}

        {activeTab === 'settings' && (
        <div className="grid grid-cols-1 gap-6">
          <section className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-bold mb-6 text-gray-900">Global Settings</h2>

            <div className="flex flex-wrap gap-2 mb-6">
              <button
                onClick={() => setSettingsSubTab('general')}
                className={`px-3 py-1.5 rounded text-sm font-medium border ${
                  settingsSubTab === 'general'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                General
              </button>
              <button
                onClick={() => setSettingsSubTab('volunteer')}
                className={`px-3 py-1.5 rounded text-sm font-medium border ${
                  settingsSubTab === 'volunteer'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Volunteer Flow
              </button>
              <button
                onClick={() => setSettingsSubTab('notifications')}
                className={`px-3 py-1.5 rounded text-sm font-medium border ${
                  settingsSubTab === 'notifications'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Notifications
              </button>
              <button
                onClick={() => setSettingsSubTab('planning')}
                className={`px-3 py-1.5 rounded text-sm font-medium border ${
                  settingsSubTab === 'planning'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Planning
              </button>
              <button
                onClick={() => setSettingsSubTab('danger')}
                className={`px-3 py-1.5 rounded text-sm font-medium border ${
                  settingsSubTab === 'danger'
                    ? 'bg-red-600 text-white border-red-600'
                    : 'bg-white text-red-700 border-red-300 hover:bg-red-50'
                }`}
              >
                Danger Zone
              </button>
            </div>

            <div className="space-y-6 max-w-2xl">
              {settingsSubTab === 'general' && (
                <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Event Name</label>
                <input
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  type="text"
                  placeholder="e.g., Roskilde Festival"
                  value={globalSettings.event_name}
                  onChange={(e) => setGlobalSettings((p) => ({ ...p, event_name: e.target.value }))}
                />
                <p className="text-sm text-gray-600 mt-2">
                  The name of your event, displayed throughout the application.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Volunteer Portal URL</label>
                <input
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  type="url"
                  placeholder="e.g., https://people.roskilde-festival.dk/volunteer"
                  value={globalSettings.peoplevol_portal_url}
                  onChange={(e) => setGlobalSettings((p) => ({ ...p, peoplevol_portal_url: e.target.value }))}
                />
                <p className="text-sm text-gray-600 mt-2">
                  The URL to the external volunteer portal. This link is shown on the signup page.
                </p>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">MailChimp Sync</h3>
                <p className="text-sm text-gray-600">Sync all active volunteers and supervisors to your MailChimp audience.</p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">MailChimp API Key</label>
                  <input
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    type="password"
                    placeholder="e.g., xxxxx-us21"
                    value={globalSettings.mailchimp_api_key}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, mailchimp_api_key: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">MailChimp Audience ID</label>
                  <input
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    type="text"
                    placeholder="e.g., a1b2c3d4e5"
                    value={globalSettings.mailchimp_audience_id}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, mailchimp_audience_id: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">MailChimp Server Prefix (optional)</label>
                  <input
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    type="text"
                    placeholder="e.g., us21"
                    value={globalSettings.mailchimp_server_prefix}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, mailchimp_server_prefix: e.target.value }))}
                  />
                  <p className="text-xs text-gray-500 mt-1">If empty, the server prefix is derived from API key suffix (e.g. key ending in -us21).</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">MailChimp Sync Tag</label>
                  <input
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    type="text"
                    placeholder="e.g., RF_ACTIVE_2026"
                    value={globalSettings.mailchimp_sync_tag}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, mailchimp_sync_tag: e.target.value }))}
                  />
                  <p className="text-xs text-gray-500 mt-1">All synced contacts are tagged with this value. Use this tag to target only this synced segment in campaigns.</p>
                </div>

                <div className="flex items-center gap-4">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded"
                      checked={mailchimpDryRun}
                      onChange={(e) => setMailchimpDryRun(e.target.checked)}
                    />
                    Dry run (preview only, no MailChimp changes)
                  </label>
                  <button
                    onClick={handleSyncMailchimpActive}
                    disabled={mailchimpSyncing || !globalSettings.mailchimp_sync_tag?.trim()}
                    className="bg-indigo-600 text-white py-2 px-6 rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
                  >
                    {mailchimpSyncing ? 'Syncing...' : mailchimpDryRun ? 'Preview MailChimp Sync' : 'Sync Active Volunteers/Supervisors to MailChimp'}
                  </button>
                  {mailchimpSyncResult && (
                    <span className="text-sm text-gray-700">
                      Tag: {mailchimpSyncResult.tag || globalSettings.mailchimp_sync_tag} · {mailchimpSyncResult.dry_run ? `Would Add: ${mailchimpSyncResult.would_sync || 0} · Would Remove: ${mailchimpSyncResult.would_remove || 0}` : `Total: ${mailchimpSyncResult.total} · Synced: ${mailchimpSyncResult.synced} · Tag Removed: ${mailchimpSyncResult.removed || 0}`} · Failed: {mailchimpSyncResult.failed} · Remove Failed: {mailchimpSyncResult.removal_failed || 0}
                    </span>
                  )}
                </div>
              </div>

                </>
              )}

              {settingsSubTab === 'volunteer' && (
                <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Hours Between Shifts</label>
                <input
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  type="number"
                  min={0}
                  placeholder="e.g., 7"
                  value={globalSettings.min_hours_between_shifts}
                  onChange={(e) => setGlobalSettings((p) => ({ ...p, min_hours_between_shifts: Number(e.target.value) || 0 }))}
                />
                <p className="text-sm text-gray-600 mt-2">
                  Requires this many hours of rest between shifts for all volunteers. Set to 0 to disable.
                </p>
              </div>

              <div className="space-y-4">
                <label className="inline-flex items-center gap-3 text-gray-800">
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded"
                    checked={!!globalSettings.show_shift_title}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, show_shift_title: e.target.checked }))}
                  />
                  <div>
                    <p className="font-medium">Show shift title</p>
                    <p className="text-sm text-gray-600">Control whether volunteers can see shift titles.</p>
                  </div>
                </label>

                <label className="inline-flex items-center gap-3 text-gray-800">
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded"
                    checked={!!globalSettings.show_shift_location}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, show_shift_location: e.target.checked }))}
                  />
                  <div>
                    <p className="font-medium">Show meeting point</p>
                    <p className="text-sm text-gray-600">Hide this to keep meeting points undisclosed until closer to the shift.</p>
                  </div>
                </label>

                <label className="inline-flex items-center gap-3 text-gray-800">
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded"
                    checked={!!globalSettings.show_next_shift_box}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, show_next_shift_box: e.target.checked }))}
                  />
                  <div>
                    <p className="font-medium">Show next shift box on homepage</p>
                    <p className="text-sm text-gray-600">Control whether volunteers see their current or upcoming shift on the dashboard.</p>
                  </div>
                </label>

                <label className="inline-flex items-center gap-3 text-gray-800">
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded"
                    checked={!!globalSettings.show_available_spots}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, show_available_spots: e.target.checked }))}
                  />
                  <div>
                    <p className="font-medium">Show available spots</p>
                    <p className="text-sm text-gray-600">Control whether volunteers can see how many spots are taken on each shift.</p>
                  </div>
                </label>

                <label className="inline-flex items-center gap-3 text-gray-800">
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded"
                    checked={!!globalSettings.show_waitlist_position}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, show_waitlist_position: e.target.checked }))}
                  />
                  <div>
                    <p className="font-medium">Show waiting list position</p>
                    <p className="text-sm text-gray-600">Show waitlisted volunteers their position in the queue.</p>
                  </div>
                </label>

                <label className="inline-flex items-center gap-3 text-gray-800">
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded"
                    checked={!!globalSettings.volunteer_login_waitlist_modal_enabled}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, volunteer_login_waitlist_modal_enabled: e.target.checked }))}
                  />
                  <div>
                    <p className="font-medium">Show long waiting list modal on volunteer login</p>
                    <p className="text-sm text-gray-600">Displays a recommendation modal on /volunteer-login with a link to other RF teams.</p>
                  </div>
                </label>

                <label className="inline-flex items-center gap-3 text-gray-800">
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded"
                    checked={!!globalSettings.volunteer_login_new_signups_closed}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, volunteer_login_new_signups_closed: e.target.checked }))}
                  />
                  <div>
                    <p className="font-medium">Close volunteer login for new signups</p>
                    <p className="text-sm text-gray-600">Blocks login-link requests from unknown email addresses and shows a notice on /volunteer-login.</p>
                  </div>
                </label>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp Group Join Link</label>
                  <input
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    type="url"
                    placeholder="https://chat.whatsapp.com/..."
                    value={globalSettings.whatsapp_group_join_url}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, whatsapp_group_join_url: e.target.value }))}
                  />
                  <p className="text-xs text-gray-500 mt-1">If left empty, the dashboard WhatsApp modal will not be shown.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp Group Confirmation Code</label>
                  <input
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    type="text"
                    placeholder="Code from the WhatsApp group description"
                    value={globalSettings.whatsapp_group_confirmation_code}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, whatsapp_group_confirmation_code: e.target.value }))}
                  />
                  <p className="text-xs text-gray-500 mt-1">The volunteer must enter this code to confirm they joined the group and hide the warning modal.</p>
                </div>

                <div className="space-y-2">
                  <label className="block">
                    <span className="text-sm font-medium text-gray-800">Waiting List Offer Hours</span>
                    <input
                      type="number"
                      min="1"
                      max="168"
                      value={globalSettings.waitlist_offer_hours}
                      onChange={(e) => setGlobalSettings((p) => ({ ...p, waitlist_offer_hours: parseInt(e.target.value) || 48 }))}
                      className="mt-1 border rounded px-3 py-2 w-full max-w-xs"
                    />
                    <p className="text-sm text-gray-600 mt-1">Hours a volunteer has to respond to a waiting list offer before it expires.</p>
                  </label>
                </div>

                <div className="space-y-2">
                  <label className="block">
                    <span className="text-sm font-medium text-gray-800">Shift Access Level</span>
                  </label>
                  <select
                    value={globalSettings.booking_enabled}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, booking_enabled: e.target.value as 'no-access' | 'view-only' | 'booking-enabled' }))}
                    className="border rounded px-3 py-2 w-full max-w-xs"
                  >
                    <option value="no-access">No access to shifts</option>
                    <option value="view-only">View only</option>
                    <option value="booking-enabled">Booking enabled</option>
                  </select>
                  <p className="text-sm text-gray-600">Control volunteer access to the shift booking interface.</p>
                </div>

                <label className="inline-flex items-center gap-3 text-gray-800">
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded"
                    checked={globalSettings.sick_note_enabled === true}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, sick_note_enabled: e.target.checked }))}
                  />
                  <div>
                    <p className="font-medium">Enable sick note reporting</p>
                    <p className="text-sm text-gray-600">Enable this when the festival starts to allow volunteers to report illness.</p>
                  </div>
                </label>

                <label className="inline-flex items-center gap-3 text-gray-800">
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded"
                    checked={globalSettings.withdrawal_permitted === true}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, withdrawal_permitted: e.target.checked }))}
                  />
                  <div>
                    <p className="font-medium">Allow withdrawal requests</p>
                    <p className="text-sm text-gray-600">When disabled, volunteers with booked shifts cannot submit withdrawal requests. They must find replacements instead.</p>
                  </div>
                </label>
              </div>
                </>
              )}

              {settingsSubTab === 'notifications' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Email Notifications</h3>
                <p className="text-sm text-gray-600">Choose which events should trigger notification emails.</p>
                <p className="text-xs text-gray-500">
                  Available placeholders: %event%, %firstname%, %role%, %frontend_url%, %hours_to_deadline%, %expiry_date%, %plan%, %shift_title%, %shift_time%, %status_line%, %reschedule_note%, %admin_notes%.
                </p>

                <label className="inline-flex items-center gap-3 text-gray-800">
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded"
                    checked={globalSettings.notification_application_submitted === true}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, notification_application_submitted: e.target.checked }))}
                  />
                  <div>
                    <p className="font-medium">Application submitted</p>
                    <p className="text-sm text-gray-600">Notify volunteers when their application is received.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowNotificationTemplates((p) => ({ ...p, application_submitted: !p.application_submitted }))}
                    className="ml-auto px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border border-gray-300"
                  >
                    {showNotificationTemplates.application_submitted ? 'Hide message' : 'Edit message'}
                  </button>
                </label>
                {showNotificationTemplates.application_submitted && (
                  <textarea
                    rows={6}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="Custom message for application submitted"
                    value={globalSettings.notification_template_application_submitted || ''}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, notification_template_application_submitted: e.target.value }))}
                  />
                )}

                <label className="inline-flex items-center gap-3 text-gray-800">
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded"
                    checked={globalSettings.notification_application_approved === true}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, notification_application_approved: e.target.checked }))}
                  />
                  <div>
                    <p className="font-medium">Application approved</p>
                    <p className="text-sm text-gray-600">Notify volunteers when their application is approved.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowNotificationTemplates((p) => ({ ...p, application_approved: !p.application_approved }))}
                    className="ml-auto px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border border-gray-300"
                  >
                    {showNotificationTemplates.application_approved ? 'Hide message' : 'Edit message'}
                  </button>
                </label>
                {showNotificationTemplates.application_approved && (
                  <textarea
                    rows={6}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="Custom message for application approved"
                    value={globalSettings.notification_template_application_approved || ''}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, notification_template_application_approved: e.target.value }))}
                  />
                )}

                <label className="inline-flex items-center gap-3 text-gray-800">
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded"
                    checked={globalSettings.notification_application_moved_to_waitlist === true}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, notification_application_moved_to_waitlist: e.target.checked }))}
                  />
                  <div>
                    <p className="font-medium">Application moved to waiting list</p>
                    <p className="text-sm text-gray-600">Notify volunteers when their application is added to the waiting list.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowNotificationTemplates((p) => ({ ...p, application_moved_to_waitlist: !p.application_moved_to_waitlist }))}
                    className="ml-auto px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border border-gray-300"
                  >
                    {showNotificationTemplates.application_moved_to_waitlist ? 'Hide message' : 'Edit message'}
                  </button>
                </label>
                {showNotificationTemplates.application_moved_to_waitlist && (
                  <textarea
                    rows={6}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="Custom message for application moved to waiting list"
                    value={globalSettings.notification_template_application_moved_to_waitlist || ''}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, notification_template_application_moved_to_waitlist: e.target.value }))}
                  />
                )}

                <label className="inline-flex items-center gap-3 text-gray-800">
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded"
                    checked={globalSettings.notification_waitlist_offer_sent === true}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, notification_waitlist_offer_sent: e.target.checked }))}
                  />
                  <div>
                    <p className="font-medium">Volunteer spot available (waitlist offer)</p>
                    <p className="text-sm text-gray-600">Notify volunteers when a spot opens up for them.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowNotificationTemplates((p) => ({ ...p, waitlist_offer_sent: !p.waitlist_offer_sent }))}
                    className="ml-auto px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border border-gray-300"
                  >
                    {showNotificationTemplates.waitlist_offer_sent ? 'Hide message' : 'Edit message'}
                  </button>
                </label>
                {showNotificationTemplates.waitlist_offer_sent && (
                  <textarea
                    rows={6}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="Custom message for volunteer spot available"
                    value={globalSettings.notification_template_waitlist_offer_sent || ''}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, notification_template_waitlist_offer_sent: e.target.value }))}
                  />
                )}

                <label className="inline-flex items-center gap-3 text-gray-800">
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded"
                    checked={globalSettings.notification_sick_note_submitted === true}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, notification_sick_note_submitted: e.target.checked }))}
                  />
                  <div>
                    <p className="font-medium">Sick note submitted</p>
                    <p className="text-sm text-gray-600">Notify volunteers when their sick note is received.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowNotificationTemplates((p) => ({ ...p, sick_note_submitted: !p.sick_note_submitted }))}
                    className="ml-auto px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border border-gray-300"
                  >
                    {showNotificationTemplates.sick_note_submitted ? 'Hide message' : 'Edit message'}
                  </button>
                </label>
                {showNotificationTemplates.sick_note_submitted && (
                  <textarea
                    rows={6}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="Custom message for sick note submitted"
                    value={globalSettings.notification_template_sick_note_submitted || ''}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, notification_template_sick_note_submitted: e.target.value }))}
                  />
                )}

                <label className="inline-flex items-center gap-3 text-gray-800">
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded"
                    checked={globalSettings.notification_sick_note_status === true}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, notification_sick_note_status: e.target.checked }))}
                  />
                  <div>
                    <p className="font-medium">Sick note approved / rejected</p>
                    <p className="text-sm text-gray-600">Notify volunteers when their sick note is approved or rejected.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowNotificationTemplates((p) => ({ ...p, sick_note_status: !p.sick_note_status }))}
                    className="ml-auto px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border border-gray-300"
                  >
                    {showNotificationTemplates.sick_note_status ? 'Hide message' : 'Edit message'}
                  </button>
                </label>
                {showNotificationTemplates.sick_note_status && (
                  <textarea
                    rows={6}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="Custom message for sick note status"
                    value={globalSettings.notification_template_sick_note_status || ''}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, notification_template_sick_note_status: e.target.value }))}
                  />
                )}

                <label className="inline-flex items-center gap-3 text-gray-800">
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded"
                    checked={globalSettings.notification_withdrawal_approved === true}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, notification_withdrawal_approved: e.target.checked }))}
                  />
                  <div>
                    <p className="font-medium">Withdrawal approved</p>
                    <p className="text-sm text-gray-600">Notify volunteers when their withdrawal is approved.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowNotificationTemplates((p) => ({ ...p, withdrawal_approved: !p.withdrawal_approved }))}
                    className="ml-auto px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border border-gray-300"
                  >
                    {showNotificationTemplates.withdrawal_approved ? 'Hide message' : 'Edit message'}
                  </button>
                </label>
                {showNotificationTemplates.withdrawal_approved && (
                  <textarea
                    rows={6}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="Custom message for withdrawal approved"
                    value={globalSettings.notification_template_withdrawal_approved || ''}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, notification_template_withdrawal_approved: e.target.value }))}
                  />
                )}

                <label className="inline-flex items-center gap-3 text-gray-800">
                  <div className="flex-1">
                    <p className="font-medium">Shifts Updated (manual)</p>
                    <p className="text-sm text-gray-600">Template for the manual "notify shifts updated" button on a volunteer's profile page.</p>
                    <p className="text-xs text-gray-500 mt-0.5">Variables: %event%, %firstname%, %name%, %email%, %frontend_url%</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowNotificationTemplates((p) => ({ ...p, shifts_updated: !p.shifts_updated }))}
                    className="ml-auto px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border border-gray-300"
                  >
                    {showNotificationTemplates.shifts_updated ? 'Hide message' : 'Edit message'}
                  </button>
                </label>
                {showNotificationTemplates.shifts_updated && (
                  <textarea
                    rows={6}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="Custom message for shifts updated notification"
                    value={globalSettings.notification_template_shifts_updated || ''}
                    onChange={(e) => setGlobalSettings((p) => ({ ...p, notification_template_shifts_updated: e.target.value }))}
                  />
                )}
              </div>
              )}

              {settingsSubTab === 'planning' && (
                <>
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Shift Booking Deadline</h3>
                <p className="text-sm text-gray-600">Set a deadline for when volunteers must complete their shift bookings. This will show a countdown on the volunteer dashboard.</p>
                
                <div>
                  <label className="block mb-2">
                    <span className="text-sm font-medium text-gray-700">Booking Deadline Date & Time</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="datetime-local"
                      value={globalSettings.booking_deadline || ''}
                      onChange={(e) => setGlobalSettings((p) => ({ ...p, booking_deadline: e.target.value || null }))}
                      className="border rounded px-3 py-2"
                    />
                    {globalSettings.booking_deadline && (
                      <button
                        onClick={() => setGlobalSettings((p) => ({ ...p, booking_deadline: null }))}
                        className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border border-gray-300"
                        title="Clear deadline"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  {globalSettings.booking_deadline && (
                    <p className="text-xs text-gray-500 mt-1">
                      Deadline: {new Date(globalSettings.booking_deadline).toLocaleString('en-GB', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric', 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Withdrawal Request Deadline</h3>
                <p className="text-sm text-gray-600">Set a deadline for when volunteers must submit withdrawal requests.</p>
                
                <div>
                  <label className="block mb-2">
                    <span className="text-sm font-medium text-gray-700">Withdrawal Deadline Date</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={globalSettings.withdrawal_deadline || ''}
                      onChange={(e) => setGlobalSettings((p) => ({ ...p, withdrawal_deadline: e.target.value || null }))}
                      className="border rounded px-3 py-2"
                    />
                    {globalSettings.withdrawal_deadline && (
                      <button
                        onClick={() => setGlobalSettings((p) => ({ ...p, withdrawal_deadline: null }))}
                        className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border border-gray-300"
                        title="Clear deadline"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  {globalSettings.withdrawal_deadline && (
                    <p className="text-xs text-gray-500 mt-1">
                      Deadline: {new Date(globalSettings.withdrawal_deadline).toLocaleDateString('en-GB', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Booking Capacity Percentage</h3>
                <p className="text-sm text-gray-600">Control how many volunteer spots are shown as available when booking. This only affects volunteer capacity and always rounds up.</p>

                <div>
                  <label className="block mb-2">
                    <span className="text-sm font-medium text-gray-700">Volunteer capacity shown (%)</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={globalSettings.booking_capacity_percent}
                      onChange={(e) => setGlobalSettings((p) => ({ ...p, booking_capacity_percent: Math.max(0, Math.min(100, Number(e.target.value) || 0)) }))}
                      className="border rounded px-3 py-2 w-24"
                    />
                    <span className="text-sm text-gray-600">%</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Example: 10 volunteer spots at 50% shows 5 spots. Once 5 volunteers book, the shift appears full.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Assignment Fill Color Thresholds</h3>
                <p className="text-sm text-gray-600">Configure when shift assignments change color based on fill percentage. Colors help identify shifts needing attention.</p>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block mb-2">
                      <span className="text-sm font-medium text-gray-700">Critical (Red)</span>
                      <p className="text-xs text-gray-500">Less than this % filled</p>
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={globalSettings.fill_threshold_critical}
                        onChange={(e) => setGlobalSettings((p) => ({ ...p, fill_threshold_critical: Math.max(0, Math.min(100, Number(e.target.value) || 0)) }))}
                        className="border rounded px-3 py-2 w-20"
                      />
                      <span className="text-sm text-gray-600">%</span>
                      <div className="w-6 h-6 bg-red-50 border-2 border-red-300 rounded"></div>
                    </div>
                  </div>

                  <div>
                    <label className="block mb-2">
                      <span className="text-sm font-medium text-gray-700">Warning (Orange)</span>
                      <p className="text-xs text-gray-500">Less than this % filled</p>
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={globalSettings.fill_threshold_warning}
                        onChange={(e) => setGlobalSettings((p) => ({ ...p, fill_threshold_warning: Math.max(0, Math.min(100, Number(e.target.value) || 0)) }))}
                        className="border rounded px-3 py-2 w-20"
                      />
                      <span className="text-sm text-gray-600">%</span>
                      <div className="w-6 h-6 bg-orange-50 border-2 border-orange-300 rounded"></div>
                    </div>
                  </div>

                  <div>
                    <label className="block mb-2">
                      <span className="text-sm font-medium text-gray-700">Low (Yellow)</span>
                      <p className="text-xs text-gray-500">Less than this % filled</p>
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={globalSettings.fill_threshold_low}
                        onChange={(e) => setGlobalSettings((p) => ({ ...p, fill_threshold_low: Math.max(0, Math.min(100, Number(e.target.value) || 0)) }))}
                        className="border rounded px-3 py-2 w-20"
                      />
                      <span className="text-sm text-gray-600">%</span>
                      <div className="w-6 h-6 bg-yellow-50 border-2 border-yellow-300 rounded"></div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-900">
                    <strong>Example:</strong> With thresholds at {globalSettings.fill_threshold_critical}%, {globalSettings.fill_threshold_warning}%, {globalSettings.fill_threshold_low}%:
                    <span className="block mt-1">
                      &lt;{globalSettings.fill_threshold_critical}% = <span className="text-red-700 font-semibold">Red</span> • 
                      {globalSettings.fill_threshold_critical}-{globalSettings.fill_threshold_warning}% = <span className="text-orange-700 font-semibold">Orange</span> • 
                      {globalSettings.fill_threshold_warning}-{globalSettings.fill_threshold_low}% = <span className="text-yellow-700 font-semibold">Yellow</span> • 
                      ≥{globalSettings.fill_threshold_low}% = <span className="text-green-700 font-semibold">Green</span>
                    </span>
                  </p>
                </div>
              </div>
                </>
              )}

              {settingsSubTab === 'danger' && (
              <div className="border-4 border-red-600 rounded-lg p-6 bg-red-50 space-y-4">
                <div className="flex items-center gap-3">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <h3 className="text-xl font-bold text-red-900">Danger Zone</h3>
                </div>
                <p className="text-sm text-red-800">
                  These actions are <strong>irreversible</strong> and will permanently delete data from the database.
                </p>
                
                <div className="bg-white border-2 border-red-400 rounded p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Delete All Shifts</h4>
                  <p className="text-sm text-gray-700 mb-3">
                    This will permanently delete all shifts and their associated bookings. This cannot be undone.
                    Current shift count: <strong>{shifts.length}</strong>
                  </p>
                  <button
                    onClick={() => setShowDeleteAllModal(true)}
                    className="bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 font-medium"
                  >
                    Delete All Shifts
                  </button>
                </div>

                <div className="bg-white border-2 border-red-400 rounded p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Delete Inactive/Guest Users</h4>
                  <p className="text-sm text-gray-700 mb-3">
                    This will permanently delete all guest users and inactive users (withdrawn or rejected). This cannot be undone.
                    Matching users: <strong>{inactiveGuestCount}</strong>
                  </p>
                  <button
                    onClick={handleDeleteInactiveUsers}
                    className="bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 font-medium"
                    disabled={inactiveGuestCount === 0}
                  >
                    Delete Inactive/Guest Users
                  </button>
                </div>

                <div className="bg-white border-2 border-red-400 rounded p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Delete All Non-Admin Users</h4>
                  <p className="text-sm text-gray-700 mb-3">
                    This will permanently delete all users except admins. This cannot be undone.
                    Matching users: <strong>{nonAdminCount}</strong>
                  </p>
                  <button
                    onClick={() => setShowDeleteNonAdminModal(true)}
                    className="bg-red-700 text-white py-2 px-4 rounded hover:bg-red-800 font-medium"
                    disabled={nonAdminCount === 0}
                  >
                    Delete All Non-Admin Users
                  </button>
                </div>
              </div>
              )}

              <div className="flex items-center gap-4">
                <button
                  onClick={handleSaveGlobalSettings}
                  className="bg-blue-600 text-white py-2 px-6 rounded-lg hover:bg-blue-700 font-medium"
                >
                  Save Settings
                </button>
                {settingsSaved && (
                  <span className="text-green-600 font-medium">✓ Settings saved</span>
                )}
              </div>
            </div>
          </section>
        </div>
        )}

        {activeTab === 'news' && (
        <div className="grid grid-cols-1 gap-6">
          <section className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-bold mb-4 text-gray-900">Volunteer News</h2>

            <div className="space-y-4 max-w-3xl mb-8">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                <input
                  type="text"
                  value={newsForm.title}
                  onChange={(e) => setNewsForm((prev) => ({ ...prev, title: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Post title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
                <textarea
                  value={newsForm.content}
                  onChange={(e) => setNewsForm((prev) => ({ ...prev, content: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 min-h-[140px]"
                  placeholder="Write update/news content..."
                />
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={newsForm.is_published}
                  onChange={(e) => setNewsForm((prev) => ({ ...prev, is_published: e.target.checked }))}
                />
                Published (visible on volunteer dashboard)
              </label>
              <div className="flex gap-2">
                <button
                  onClick={handleCreateOrUpdateNewsPost}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  {editingNewsId ? 'Update Post' : 'Create Post'}
                </button>
                {editingNewsId && (
                  <button
                    onClick={handleCancelNewsEdit}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-3">
              {newsPosts.length === 0 ? (
                <p className="text-sm text-gray-500">No posts yet.</p>
              ) : (
                newsPosts.map((post) => (
                  <div key={post.id} className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-semibold text-gray-900">{post.title}</h3>
                        <p className="text-xs text-gray-500 mt-1">
                          {post.is_published === false ? 'Draft' : 'Published'} · {new Date(post.created_at).toLocaleString('en-GB')}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditNewsPost(post)}
                          className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteNewsPost(post.id)}
                          className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 mt-3 whitespace-pre-wrap">{renderTextWithLinks(post.content)}</p>
                  </div>
                ))
              )}
            </div>
          </section>
        </div>
        )}

        {/* Admin To-Do List — always visible */}
        <section className="bg-white shadow rounded p-4 mt-8">
          <h2 className="text-lg font-semibold mb-3">To-Do</h2>
          <form onSubmit={handleAddTodo} className="flex gap-2 mb-4">
            <input
              type="text"
              value={todoInput}
              onChange={(e) => setTodoInput(e.target.value)}
              placeholder="Add a task..."
              className="flex-1 border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              type="submit"
              disabled={!todoInput.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:bg-gray-300"
            >
              Add
            </button>
          </form>
          {todos.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No tasks — all done!</p>
          ) : (
            <ul className="space-y-2">
              {todos.map((todo) => (
                <li key={todo.id} className="flex items-center gap-3 group">
                  <input
                    type="checkbox"
                    onChange={() => handleDeleteTodo(todo.id)}
                    className="w-4 h-4 cursor-pointer accent-green-600"
                  />
                  <span className="text-sm text-gray-800 flex-1">{todo.text}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>

      {/* Delete All Shifts Confirmation Modals */}
      {showDeleteAllModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h3 className="text-xl font-bold text-gray-900">Confirm Deletion</h3>
            </div>
            <p className="text-gray-700 mb-4">
              This will permanently delete <strong>ALL {shifts.length} shifts</strong> and all associated bookings.
              This action <strong className="text-red-600">CANNOT BE UNDONE</strong>.
            </p>
            <p className="text-gray-700 mb-4">
              Type <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">DELETE ALL SHIFTS</code> to confirm:
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 mb-4"
              placeholder="DELETE ALL SHIFTS"
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  if (deleteConfirmText === 'DELETE ALL SHIFTS') {
                    setShowFinalDeleteConfirm(true);
                  }
                }}
                disabled={deleteConfirmText !== 'DELETE ALL SHIFTS'}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
              >
                Continue
              </button>
              <button
                onClick={() => {
                  setShowDeleteAllModal(false);
                  setDeleteConfirmText('');
                }}
                className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded hover:bg-gray-300 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showFinalDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h3 className="text-xl font-bold text-red-900">Final Confirmation</h3>
            </div>
            <p className="text-gray-900 mb-6 text-lg">
              Are you <strong>absolutely sure</strong>? This will permanently delete <strong className="text-red-600">{shifts.length} shifts</strong> and cannot be recovered.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDeleteAllShifts}
                className="flex-1 bg-red-600 text-white py-3 px-4 rounded hover:bg-red-700 font-bold text-lg"
              >
                Yes, Delete Everything
              </button>
              <button
                onClick={() => {
                  setShowFinalDeleteConfirm(false);
                  setShowDeleteAllModal(false);
                  setDeleteConfirmText('');
                }}
                className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded hover:bg-gray-300 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete All Non-Admin Users Confirmation Modals */}
      {showDeleteNonAdminModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h3 className="text-xl font-bold text-gray-900">Confirm Deletion</h3>
            </div>
            <p className="text-gray-700 mb-4">
              This will permanently delete <strong>ALL {nonAdminCount} non-admin users</strong> and all associated data.
              This action <strong className="text-red-600">CANNOT BE UNDONE</strong>.
            </p>
            <p className="text-gray-700 mb-4">
              Type <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">DELETE ALL NON-ADMIN USERS</code> to confirm:
            </p>
            <input
              type="text"
              value={deleteNonAdminConfirmText}
              onChange={(e) => setDeleteNonAdminConfirmText(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 mb-4"
              placeholder="DELETE ALL NON-ADMIN USERS"
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  if (deleteNonAdminConfirmText === 'DELETE ALL NON-ADMIN USERS') {
                    setShowFinalDeleteNonAdminConfirm(true);
                  }
                }}
                disabled={deleteNonAdminConfirmText !== 'DELETE ALL NON-ADMIN USERS'}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
              >
                Continue
              </button>
              <button
                onClick={() => {
                  setShowDeleteNonAdminModal(false);
                  setDeleteNonAdminConfirmText('');
                }}
                className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded hover:bg-gray-300 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showFinalDeleteNonAdminConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h3 className="text-xl font-bold text-red-900">Final Confirmation</h3>
            </div>
            <p className="text-gray-900 mb-6 text-lg">
              Are you <strong>absolutely sure</strong>? This will permanently delete <strong className="text-red-600">{nonAdminCount} non-admin users</strong> and cannot be recovered.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDeleteNonAdminUsers}
                className="flex-1 bg-red-600 text-white py-3 px-4 rounded hover:bg-red-700 font-bold text-lg"
              >
                Yes, Delete Everything
              </button>
              <button
                onClick={() => {
                  setShowFinalDeleteNonAdminConfirm(false);
                  setShowDeleteNonAdminModal(false);
                  setDeleteNonAdminConfirmText('');
                }}
                className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded hover:bg-gray-300 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role Selection Modal */}
      {showRoleSelectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Select Role for Assignment</h3>
            <p className="text-gray-700 mb-4">
              This shift has multiple role types. Please select which role to assign the selected user(s) as:
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Assign as:</label>
              <select
                value={selectedAssignRole}
                onChange={(e) => setSelectedAssignRole(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2"
              >
                {roleSelectionOptions.map(role => (
                  <option key={role} value={role}>
                    {role.charAt(0).toUpperCase() + role.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-3">
              <button
                onClick={confirmRoleSelection}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 font-medium"
              >
                Confirm
              </button>
              <button
                onClick={() => {
                  setShowRoleSelectionModal(false);
                  setPendingAssignments([]);
                  setSelectedAssignRole('');
                  setRoleSelectionOptions([]);
                }}
                className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded hover:bg-gray-300 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showBatchInviteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Confirm Batch Invite</h3>
            <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4">
              <p className="text-sm text-gray-700 mb-2">
                <strong>Number of volunteers:</strong> {selectedWaitlistUserIds.size}
              </p>
              <p className="text-sm text-gray-700">
                <strong>Offer validity:</strong> {globalSettings?.waitlist_offer_hours || 48} hours
              </p>
              <p className="text-sm text-gray-700 mt-3">
                <strong>Important:</strong> All selected volunteers will receive offers simultaneously. The first one to accept becomes an approved volunteer, and the others will be automatically rejected and moved back in the queue.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleSendBatchInvites}
                disabled={sendingBatchOffers}
                className="flex-1 bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 disabled:bg-gray-400 font-semibold"
              >
                {sendingBatchOffers ? 'Sending...' : 'Send Batch Invites'}
              </button>
              <button
                onClick={() => setShowBatchInviteConfirm(false)}
                disabled={sendingBatchOffers}
                className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded hover:bg-gray-300 disabled:bg-gray-100 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showApproveWithdrawalModal && approveWithdrawalTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Approve Withdrawal Request</h3>
            <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4 text-sm text-gray-800">
              <p><strong>Volunteer:</strong> {approveWithdrawalTarget.user_name}</p>
              <p><strong>Email:</strong> {approveWithdrawalTarget.user_email}</p>
              <p><strong>Future shifts:</strong> {Number(approveWithdrawalTarget.future_shifts_count || 0)}</p>
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-2">Admin notes (optional)</label>
            <textarea
              value={approveWithdrawalAdminNotes}
              onChange={(e) => setApproveWithdrawalAdminNotes(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded px-3 py-2 mb-4"
              placeholder="Optional note for this decision"
            />

            {Number(approveWithdrawalTarget.future_shifts_count || 0) > 0 && (
              <label className="flex items-start gap-3 mb-4">
                <input
                  type="checkbox"
                  checked={approveWithdrawalDeleteShifts}
                  onChange={(e) => setApproveWithdrawalDeleteShifts(e.target.checked)}
                  className="mt-1 w-5 h-5"
                />
                <span className="text-base text-gray-900 leading-6">
                  Delete all future shifts for this volunteer
                  <span className="block text-red-700 font-semibold mt-1 text-sm">
                    Required: this volunteer still has future shifts, so this must be checked to approve.
                  </span>
                </span>
              </label>
            )}

            <div className="flex gap-3">
              <button
                onClick={confirmApproveWithdrawal}
                disabled={approvingWithdrawalId === approveWithdrawalTarget.id || (Number(approveWithdrawalTarget.future_shifts_count || 0) > 0 && !approveWithdrawalDeleteShifts)}
                className="flex-1 bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 disabled:bg-gray-400 font-semibold"
              >
                {approvingWithdrawalId === approveWithdrawalTarget.id ? 'Approving...' : 'Confirm Approval'}
              </button>
              <button
                onClick={closeApproveWithdrawalModal}
                disabled={approvingWithdrawalId === approveWithdrawalTarget.id}
                className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded hover:bg-gray-300 disabled:bg-gray-100 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {imagePreviewUrl && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 cursor-pointer"
          onClick={() => setImagePreviewUrl(null)}
        >
          <div 
            className="bg-white rounded-lg shadow-2xl max-w-2xl w-full p-6 cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">{imagePreviewTitle}</h3>
              <button
                onClick={() => setImagePreviewUrl(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="flex justify-center">
              <img 
                src={imagePreviewUrl} 
                alt={imagePreviewTitle}
                className="max-w-full max-h-96 rounded"
              />
            </div>
            <p className="text-xs text-gray-500 mt-4 text-center">Click outside to close</p>
          </div>
        </div>
      )}
    </>
  );
}
